// @ts-strict-ignore
/* eslint-disable no-continue */
import {
  ContainerType,
} from 'flow/store/engine/types';
import type {
  Action,
  Attribute,
  DataRoute,
  List,
  Outcome,
  PublicId,
  PublishedAttribute,
  ReasonCode,
  ReasonCodeGroup,
  ServiceObject,
  ServicesByName,
  Tag,
  Threshold,
} from 'flow/store/engine/types';
import {
  extractThresholdIds, getAllThresholdFromRules, getThresholdsForActions, indexThresholdsByPublicId,
} from '../thresholds';
import { snippetMode, snippetOperation } from 'flow/store/redux/types';
import type { SnippetMode, SnippetOperation } from 'flow/store/redux/types';
import type { GroupRule } from '../rules';
import {
  buildGroupRule,
  buildRuleTree,
  getChildRule,
  groupRulesInRuleTree,
  groupType,
  hasMultipleThresholds,
  insertRuleIntoRuleTree,
  toggleGroupTypeInRuleTree,
} from '../rules';
import type {
  SnippetVersion, SnippetIntegration, SnippetMetadata, WFElementOperationMap,
  WFElement,
} from './types';
import { extractDataRouteIds, getDataRoutesForActions, indexDataRoutesByPublicId } from '../dataRoutes';
import { buildPrimitives, getCollisionsWithinPrimitives } from './collisions';
import type { EquivalencyKey } from './collisions/equivalency';
import { getEquivalencyKeyForPrimitive } from './collisions/equivalency';
import {
  areDifferent, areEqual, computeNextName, hasNameCollision,
} from '../util';
import {
  actionHasRules,
  findHighestExistingActionPrecedence,
  getActionName,
  getActionPrecedenceIndex,
  getNonPrecedenceIndexedActions,
  getPrecedenceIndexedActions,
  getPublishedDetails,
  indexActionsByPublicId,
  isActionOfType,
  isDefaultPrecedenceIndexedAction,
  setActionName,
  setActionPrecedenceIndex,
  sortActionsByPrecedenceIndex,
} from '../actions';
import { ActionType, booleanRuleStrings } from 'flow/store/engine/constants';
import { indexTagsByName } from '../tags';
import { indexOutcomesByName } from '../outcomes';
import {
  indexAttributesByEquivalencyKey,
  createEphemeralAttributeIdAssigner,
  getAttributesForServices,
  getHighestAttributeId,
  indexAttributesByToken,
  indexServiceInternalNamesByAttributeToken,
} from '../services/attributes';
import {
  generateTemporaryPublishedAttributeToken,
  indexPublishedAttributesByName,
  isPublishedAttribute,
} from '../publishedAttributes';
import {
  createReasonCodeServiceCodeKey,
  getHighestReasonCodeGroupId,
  indexReasonCodeGroupsByName,
  indexReasonCodesByServiceCodeKey,
} from '../reasonCodes';
import { getEquivalencyKeyForAction, indexPrimitiveEquivalencyKeysByIdentifier } from './collisions/equivalency/equivalency';
import { indexServicesByInternalName, isServicesASubsetOfServicesB } from '../services';
import type { WorkflowState } from 'flow/store/redux/state';
import { incompatibleGlobalSnippetActionTypes, incompatibleLocalSnippetActionTypes } from './constants';
import { modifyRelevantDataFromActionRemoval } from 'flow/store/engine/update/actions';
import { deleteProperty } from 'dot-prop';
import { scrubServiceActions, scrubServiceCredentials } from 'flow/editor/components/SnippetCopyPaste/helpers';
import {
  getListId,
  isListAction,
} from '../actions/services/lists';
import { generateTemporaryListToken, indexListsByName, isListEphemeral } from '../lists';
import { CUSTOMER_LIST_ID_OPTION_KEY } from '../services/constants';
import {
  generateRandomizedIdPublicIdMap, invertPublicIdMap, isPublicId, remapIds,
} from 'flow/store/engine/regenerateIds';
import { createAction } from 'flow/store/engine/create/actions/actions';
import { createSnippetGateThreshold, isSnippetGateThreshold } from 'flow/store/engine/update/thresholds';
import { addThresholdToBooleanRule } from 'flow/store/engine/update/rules/rules';
import { generatePublicId } from 'flow/store/engine/utils';
import {
  getRemoteActionPublicId,
  isChildOfSnippetAction,
  getActionsRequiringGateRule,
  actionRequiresGateRule,
  getGateRuleThresholdId,
  createSnippetContainerContext,
  isExternalActionType,
  isExternalSnippetChildAction,
  appendSnippetParent,
  supportsActionMerging,
  isSourceActionMergedIntoTargetAction,
  removeSnippetParentFromChildContainer,
} from '../actions/snippets';
import { SNIPPET_SCOPE } from 'flow/editor/components/MenuBar/MenuBarModals/PolicyLibraryModal/constants';
import { getSnippetScopeFromPath } from 'flow/editor/components/helpers';

export function getIsInSnippetReviewMode(mode: SnippetMode | null) {
  return mode === snippetMode.review;
}

export function getIsInSnippetPreviewMode(mode: SnippetMode | null) {
  return mode === snippetMode.preview;
}

export function getIsInSnippetPastePendingMode(mode: SnippetMode | null) {
  return mode === snippetMode.pastePending;
}

export function getIsLinkedSnippetUpgradeOp(operation: SnippetOperation | null) {
  return operation === snippetOperation.upgradeLinkedSnippet;
}


/**
 * The editor will only have the compatible services for the given workflow type in its props.
 * This filtered data is returned from the existing Daffy GET endpoint, filtered at the DB level
 * by the query at `Alloy/Daffy/compliance/applications/versions/sql.py#SELECT_SERVICES`.
 * Hence, it is sufficient to check if all of the snippet services are included in the workflow's
 * to determine if the snippet is compatible, without needing to account for the snippet and
 * workflow type.
 */
export function doesSnippetContainIncompatibleServices(args: {
  snippet: SnippetVersion;
  accountServices: ServiceObject[];
}): boolean {
  const { accountServices, snippet } = args;

  const areAllSnippetServicesPresentInWorkflow = isServicesASubsetOfServicesB({
    servicesA: snippet.props.services,
    servicesB: accountServices,
  });

  return !areAllSnippetServicesPresentInWorkflow;
}

/**
 * Finds outcome actions that reference outcomes not present in the provided outcomes list.
 * Returns an array of invalid outcome names, or an empty array if all outcomes are valid.
 */
export function getInvalidOutcomeNames(args: {
  actions: Record<Action['publicId'], Action>;
  outcomes: Outcome[];
}): string[] {
  const { actions, outcomes } = args;
  const validOutcomeNames = new Set(outcomes.map(o => o.outcome));
  const outcomeActions = Object.values(actions).filter(
    action => isActionOfType(action, ActionType.OUTCOME),
  );

  return outcomeActions
    .filter(action => !validOutcomeNames.has(action.outcome?.name))
    .map(action => action.outcome?.name);
}

/**
 * Factory function to create a properly structured SnippetVersion from flat data.
 * Accepts the old flat structure and returns the new nested structure with data/props/metadata.
 */
export function createSnippetVersion(args: {
  actions: Record<Action['publicId'], Action>;
  thresholds: Record<Threshold['publicId'], Threshold>;
  dataRoutes: Record<DataRoute['publicId'], DataRoute>;
  tags: Tag[];
  outcomes: Outcome[];
  services: ServiceObject[];
  publishedAttributes: PublishedAttribute[];
  reasonCodeGroups: ReasonCodeGroup[];
  lists: List[];
  snippets?: SnippetMetadata[];
  metadata?: {
    token?: string;
    version?: number;
    integrations?: SnippetIntegration[];
    publishedBy?: string | null;
    publishedAt?: Date | null;
    createdBy?: string | null;
    createdAt?: Date | null;
    changeLog?: string;
    notes?: string;
  } | null;
}): SnippetVersion {
  const {
    actions,
    thresholds,
    dataRoutes,
    tags,
    outcomes,
    services,
    publishedAttributes,
    reasonCodeGroups,
    lists,
    snippets = [],
    metadata = null,
  } = args;

  return {
    data: {
      actions,
      thresholds,
      dataRoutes,
    },
    props: {
      tags,
      outcomes,
      services,
      publishedAttributes,
      reasonCodeGroups,
      lists,
      snippets,
    },
    metadata: metadata ? {
      token: metadata.token ?? '',
      version: metadata.version ?? 0,
      integrations: metadata.integrations ?? [],
      publishedBy: metadata.publishedBy ?? null,
      publishedAt: metadata.publishedAt ?? null,
      createdBy: metadata.createdBy ?? null,
      createdAt: metadata.createdAt ?? null,
      changeLog: metadata.changeLog,
      notes: metadata.notes,
    } : null,
  };
}

export function isValidActionMerge(args: {
  snippet: {
    actionToMerge: Action;
    actions: Record<Action['publicId'], Action>;
    thresholds: Record<Threshold['publicId'], Threshold>;
    dataRoutes: Record<DataRoute['publicId'], DataRoute>;
  };
  workflow: {
    actionToMergeInto: Action;
    actions: Record<Action['publicId'], Action>;
    thresholds: Record<Threshold['publicId'], Threshold>;
    dataRoutes: Record<DataRoute['publicId'], DataRoute>;
  };
  actionMergeMap: Map<Action['publicId'], Action['publicId']>;
}): boolean {
  if (!isInsertionTopologicallyValid(args)) {
    return false;
  }

  return true;
}


/**
 * Evaluates whether merging an action into a workflow causes a cycle.
 * Cycles are not supported, as our graph is strictly a Direct Acyclical Graph (DAG)
 */
function isInsertionTopologicallyValid(args: {
  snippet: {
    actionToMerge: Action;
    actions: Record<Action['publicId'], Action>;
    thresholds: Record<Threshold['publicId'], Threshold>;
    dataRoutes: Record<DataRoute['publicId'], DataRoute>;
  };
  workflow: {
    actionToMergeInto: Action;
    actions: Record<Action['publicId'], Action>;
    thresholds: Record<Threshold['publicId'], Threshold>;
    dataRoutes: Record<DataRoute['publicId'], DataRoute>;
  };
  actionMergeMap: Map<Action['publicId'], Action['publicId']>;
}): boolean {
  /**
   * The traversal algorithm is essentially breadth-first search on all action incomers,
   * with the extra complexity of action merging. To account for merging, we need
   * to look up incomers in both the snippet and the workflow graphs.
   * Steps:
   * 1. Grab all incomer actions for the snippet action in question, both from the snippet and wf.
   * 2. Evaluate to see if a incomer has been merged, if so, replace the incomer Action
   *    with the merged one from the workflow.
   * 3. Keep all incomer ids, and see if the target merge node (where we are trying to merge into)
   *    is part of the incomers. If it is, a cycle would be created, so `false` is returned.
   * 4. Add all incomers to the queue and continue evaluating with BFS. This will include
   *    previously merged incomers, if any.
   * 5. If the merge target is never found in the incomer graph, the merge is valid
   */
  const { actionMergeMap, snippet, workflow } = args;

  const queue: Action[] = [snippet.actionToMerge];
  const ancestorActionIds = new Set<Action['publicId']>();

  // Breadth-first search on all nodes connected to the sourceAction
  for (const action of queue) {
    const incomerSnippetActions = getIncomerActionsForAction({
      action,
      actions: snippet.actions,
      thresholds: snippet.thresholds,
      dataRoutes: snippet.dataRoutes,
    });

    const incomerWorkflowActions = getIncomerActionsForAction({
      action,
      actions: workflow.actions,
      thresholds: workflow.thresholds,
      dataRoutes: workflow.dataRoutes,
    });

    const allIncomerActionsForAction = new Map<Action['publicId'], Action>([
      ...Object.entries(incomerSnippetActions),
      ...Object.entries(incomerWorkflowActions),
    ]);


    // look for merged nodes in all incomers and swap them for the merged one
    for (const [mergedSnippetActionId, mergedWorkflowActionId] of actionMergeMap) {
      if (allIncomerActionsForAction.has(mergedSnippetActionId)) {
        allIncomerActionsForAction.delete(mergedSnippetActionId);

        const mergedWorkflowAction = workflow.actions[mergedWorkflowActionId];
        allIncomerActionsForAction.set(mergedWorkflowActionId, mergedWorkflowAction);
      }
    }

    const visitedActions = [...allIncomerActionsForAction.values()];

    for (const visitedAction of visitedActions) {
      ancestorActionIds.add(visitedAction.publicId);
    }


    // if the merge target is part of the ancestry, a cycle would be created
    if (ancestorActionIds.has(workflow.actionToMergeInto.publicId)) {
      return false;
    }
    queue.push(...visitedActions);
  }


  return true;
}


/**
 * Returns Record of all connected Actions into `action`
 *@example
 * A -> B -> C
 *   -> D -^
 *
 * For C, the result is:
 * {
 *  [B.publicId]: B,
 *  [D.publicId]: D
 * }
 * todo BUILD-3768 - move this to the actions service and resolve the downstream dependency cycles
 */
export function getIncomerActionsForAction(args: {
  action: Action;
  actions: Record<Action['publicId'], Action>;
  thresholds: Record<Threshold['publicId'], Threshold>;
  dataRoutes: Record<DataRoute['publicId'], DataRoute>;
}): Record<Action['publicId'], Action> {
  const {
    action, actions, thresholds, dataRoutes,
  } = args;

  const result: Record<Action['publicId'], Action> = {};
  const thresholdsForAction = getAllThresholdFromRules({
    rules: action.rules,
    thresholds,
  });

  for (const threshold of thresholdsForAction) {
    const thresholdLeftActionId = threshold.left.actionId;
    const thresholdRightActionId = threshold.right?.actionId;

    if (thresholdLeftActionId && actions[thresholdLeftActionId]) {
      result[thresholdLeftActionId] = actions[thresholdLeftActionId];
    }

    if (thresholdRightActionId && actions[thresholdRightActionId]) {
      result[thresholdRightActionId] = actions[thresholdRightActionId];
    }
  }

  const dataRoutesForAction = getDataRoutesForActions({
    action,
    dataRoutes,
  });

  /**
   * it's possible that an Action has the same incomer in a threshold and in a data route.
   * in that case, this data route lookup would overwrite the action from the thresholds lookup
   * with the same Action, so it's not an issue
   */
  for (const dataRoute of dataRoutesForAction) {
    const sourceActionId = dataRoute.sourceId;

    if (sourceActionId && actions[sourceActionId]) {
      result[sourceActionId] = actions[sourceActionId];
    }
  }

  return result;
}

/**
 * Changes the actionIds in a threshold to a the specified action id, if any
 */
export function resolveThresholdParentReferences(args: {
  thresholds: Threshold[];
  actionIdMap: Map<Action['publicId'], Action['publicId']>;
}) {
  const { actionIdMap, thresholds } = args;

  return thresholds.map(threshold => {
    const actionIdToResolve = actionIdMap.get(threshold.actionId);
    const leftActionIdToResolve = actionIdMap.get(threshold.left.actionId);
    const rightActionIdToResolve = actionIdMap.get(threshold.right?.actionId);

    // do not resolve the threshold if it does not point to any merged actions
    if (!leftActionIdToResolve && !rightActionIdToResolve && !actionIdToResolve) {
      return threshold;
    }

    const updatedThreshold = structuredClone(threshold);

    if (actionIdToResolve) {
      updatedThreshold.actionId = actionIdToResolve;
    }

    if (leftActionIdToResolve) {
      updatedThreshold.left.actionId = leftActionIdToResolve;
    }

    if (rightActionIdToResolve) {
      updatedThreshold.right.actionId = rightActionIdToResolve;
    }

    return updatedThreshold;
  });
}

export function resolveDataRouteSourceReferences(args: {
  actionIdMap: Map<Action['publicId'], Action['publicId']>;
  dataRoutes: DataRoute[];
}) {
  const { actionIdMap, dataRoutes } = args;

  return dataRoutes.map(dataRoute => {
    const sourceIdToResolve = actionIdMap.get(dataRoute.sourceId);

    // do not resolve the dataRoute if it does not point to any merged actions
    if (!sourceIdToResolve) {
      return dataRoute;
    }

    const updatedDataRoute = structuredClone(dataRoute);
    updatedDataRoute.sourceId = sourceIdToResolve;
    return updatedDataRoute;
  });
}


/**
 * Merges the rules of the snippetAction into the workflowAction.
 * For now, this is only used in the context of snippets, but it will likely be moved to a
 * separate service
 */
export function mergeActionRules(args: {
  snippetAction: Action;
  workflowAction: Action;
}) {
  const { snippetAction, workflowAction } = args;

  // built rules are guaranteed to be a Group
  const snippetActionRuleTree = buildRuleTree(snippetAction.rules) as GroupRule;

  if (!actionHasRules(workflowAction)) {
    return buildRuleTree(snippetAction.rules);
  }

  const workflowActionRuleTree = buildRuleTree(workflowAction.rules) as GroupRule;

  const snippetActionHasSingleThresholdRule = (
    !hasMultipleThresholds(snippetActionRuleTree)
  );
  const workflowActionHasSingleThresholdRule = (
    !hasMultipleThresholds(workflowActionRuleTree)
  );

  // simple case - both rules are single-threshold. only need to append a threshold under an 'or'
  if (snippetActionHasSingleThresholdRule
    && workflowActionHasSingleThresholdRule) {
    /*
     * by default, single-threshold rules are contained in an 'and'. since we always want an 'or',
     * we can safely toggle the group
     */
    const toggledRule = toggleGroupTypeInRuleTree({
      treeRoot: workflowActionRuleTree,
      rule: workflowActionRuleTree,
    }) as GroupRule;

    // merge the first (only) threshold rule of the snippet 'after' the first (only) rule of the group
    const mergedRule = insertRuleIntoRuleTree(
      toggledRule,
      {
        position: 'after',
        relativelyPositionedRule: getChildRule(toggledRule, 0),
        ruleToInsert: getChildRule(snippetActionRuleTree, 0),
      },
    );

    return mergedRule;
  }

  // top-level merge groups should be 'or'
  let toggledRuleTree = workflowActionRuleTree;
  if (workflowActionRuleTree.data.groupType === groupType.and) {
    toggledRuleTree = toggleGroupTypeInRuleTree({
      rule: workflowActionRuleTree,
      treeRoot: workflowActionRuleTree,
    }) as GroupRule;
  }

  const groupToAdd = buildGroupRule({
    groupType: workflowActionHasSingleThresholdRule
      ? groupType.and
      : workflowActionRuleTree.data.groupType,
  });

  // group whatever the workflow action has in a new group
  const toggledGroupedRuleTree = groupRulesInRuleTree(
    toggledRuleTree,
    {
      groupEndIndex: toggledRuleTree.data.children.length,
      groupStartIndex: 0,
      groupToInsert: groupToAdd,
      parentRule: toggledRuleTree,
    },
  ) as GroupRule;

  // add the snippet rules as a sibling of the now-grouped workflow action rules
  const mergedRuleTree = insertRuleIntoRuleTree(
    toggledGroupedRuleTree,
    {
      position: 'after',
      relativelyPositionedRule: groupToAdd,
      ruleToInsert: snippetActionRuleTree,
    },
  );
  return mergedRuleTree;
}


/**
 * Returns a copy of the snippet with its actions sorted in a valid topological order
 * todo: consider moving the internal function to the Actions service
 */
export function sortSnippetInTopologicalOrder(args: {
  readonly snippet: SnippetVersion;
}): SnippetVersion {
  /**
   * The algorithm is depth-first search to count the most ancestors an action has in all of its
   * ancestor paths, and sorting the actions within the snippet by that ancestor count in ASC order
   */

  const { snippet } = args;

  // counts the max number of ancestors an action has and stores the count in a counter Map
  function countAncestorActions(action: Action, ancestorCounter: Map<Action['publicId'], number>) {
    // memoization to prevent computing an already-visited action
    if (ancestorCounter.has(action.publicId)) {
      return ancestorCounter.get(action.publicId);
    }

    const incomerActions = getIncomerActionsForAction({
      action,
      actions: snippet.data.actions,
      thresholds: snippet.data.thresholds,
      dataRoutes: snippet.data.dataRoutes,
    });

    const hasIncomers = Object.keys(incomerActions).length > 0;
    if (!hasIncomers) {
      const baseCaseResult = 0;
      ancestorCounter.set(action.publicId, baseCaseResult);
      return 0;
    }

    let maxAncestorCount = 0;
    for (const incomerAction of Object.values(incomerActions)) {
      const ancestorCount = countAncestorActions(incomerAction, ancestorCounter) + 1;
      maxAncestorCount = Math.max(maxAncestorCount, ancestorCount);
    }

    ancestorCounter.set(action.publicId, maxAncestorCount);
    return maxAncestorCount;
  }

  const actionAncestorCounter = new Map<Action['publicId'], number>();
  for (const action of Object.values(snippet.data.actions)) {
    // count the max ancestor (incomer) path for every action in the snippet
    countAncestorActions(action, actionAncestorCounter);
  }


  // sort actions based on how many ancestors they have, in ascending order
  const sortedActionsArray = Object.values(snippet.data.actions).sort((actionA, actionB) => {
    const ancestorCountForA = actionAncestorCounter.get(actionA.publicId);
    const ancestorCountForB = actionAncestorCounter.get(actionB.publicId);

    if (ancestorCountForA === undefined || ancestorCountForB === undefined) {
      throw new Error('something went wrong while sorting snippet');
    }
    return ancestorCountForA - ancestorCountForB;
  });

  const sortedActions = Object.fromEntries(
    sortedActionsArray.map(action => [action.publicId, action]),
  );

  return createSnippetVersion({
    actions: sortedActions,
    dataRoutes: snippet.data.dataRoutes,
    thresholds: snippet.data.thresholds,
    outcomes: snippet.props.outcomes,
    services: snippet.props.services,
    tags: snippet.props.tags,
    publishedAttributes: snippet.props.publishedAttributes,
    reasonCodeGroups: snippet.props.reasonCodeGroups,
    lists: snippet.props.lists,
    metadata: snippet.metadata,
    snippets: snippet.props.snippets,
  });
}

/**
 * Filters workflow props to only include items that are actually used by actions in the snippet.
 */
export function filterPropsUsedByActions({
  actions,
  thresholds,
  workflowProps,
}: {
  actions: WorkflowState['workflowData']['actions'];
  thresholds: WorkflowState['workflowData']['thresholds'];
  workflowProps: Pick<
  WorkflowState['workflowProps'],
  'tags' | 'outcomes' | 'services' | 'publishedAttributes' | 'reasonCodeGroups' | 'lists'
  >;
}) {
  const actionsArray = Object.values(actions);
  const thresholdsArray = Object.values(thresholds);

  // Collect identifiers for each prop type used in actions
  const usedServiceInternalNames = new Set<string>();
  const usedTagNames = new Set<string>();
  const usedOutcomeNames = new Set<string>();
  const usedPublishedAttributeTokens = new Set<string>();
  const usedReasonCodeGroupNames = new Set<string>();
  // converting list ids to strings since pasted lists w/o conflicts use a token as the listId
  const usedListIds = new Set<string>();

  actionsArray.forEach(action => {
    switch (action.type) {
      case ActionType.SERVICE:
        if (action.service?.internalName) {
          usedServiceInternalNames.add(action.service.internalName);
        }
        // list services point to specific customer-owned lists in props - need separate handling
        if (isListAction(action) && getListId(action)) {
          usedListIds.add(String(getListId(action)));
        }
        break;

      case ActionType.TAG:
        if (action.tag?.name) {
          usedTagNames.add(action.tag.name);
        }
        break;

      case ActionType.OUTCOME:
        if (action.outcome?.name) {
          usedOutcomeNames.add(action.outcome.name);
        }
        break;

      case ActionType.INPUT:
        if (action.input?.publishedAttributeToken) {
          usedPublishedAttributeTokens.add(action.input.publishedAttributeToken);
        }
        break;

      case ActionType.OUTPUT:
        if (action.output?.publishedAttributeToken) {
          usedPublishedAttributeTokens.add(action.output.publishedAttributeToken);
        }
        break;

      default:
        break;
    }
  });

  thresholdsArray.forEach(threshold => {
    if (threshold.right?.reasonCodeGroup) {
      usedReasonCodeGroupNames.add(threshold.right.reasonCodeGroup.name);
    }
  });

  return {
    services: Object.values(workflowProps.services).filter(
      service => usedServiceInternalNames.has(service.internal_name),
    ),
    tags: workflowProps.tags.filter(
      tag => usedTagNames.has(tag.tag_name),
    ),
    outcomes: workflowProps.outcomes.filter(
      outcome => usedOutcomeNames.has(outcome.outcome),
    ),
    publishedAttributes: workflowProps.publishedAttributes.filter(
      pa => usedPublishedAttributeTokens.has(pa.attributeToken),
    ),
    reasonCodeGroups: workflowProps.reasonCodeGroups.filter(
      rcg => usedReasonCodeGroupNames.has(rcg.name),
    ),
    lists: workflowProps.lists.filter(
      list => usedListIds.has(String(list.customer_list_id)),
    ),
  };
}

export function isActionSnippetCompatible(action: Action) {
  const isLocalSnippet = getSnippetScopeFromPath(location.pathname) === SNIPPET_SCOPE.LOCAL;
  if (isLocalSnippet) {
    return !isActionOfType(action, incompatibleLocalSnippetActionTypes);
  }
  return !isActionOfType(action, incompatibleGlobalSnippetActionTypes);
}

export function canCopyAction(action: Action) {
  return !isActionOfType(action, ActionType.PREVIEW);
}

/** Returns the actions within a snippet that fail the supplied test. */
export function getIncompatibleActions(args: {
  snippet: SnippetVersion;
  test: (action: Action) => boolean;
}): Action[] {
  const { snippet: { data: { actions } }, test } = args;

  return (
    Object.values(actions).filter(action => !test(action))
  );
}

export function pruneSnippetVersion(args: {
  snippet: SnippetVersion;
  actionsToPrune: Action[];
}): SnippetVersion {
  const { snippet, actionsToPrune } = args;

  const prunedData = structuredClone(snippet.data);
  actionsToPrune.forEach(action => {
    deleteProperty(prunedData.actions, action.publicId);
    modifyRelevantDataFromActionRemoval({
      originalTargetValue: action,
      workflowData: prunedData,
    });
  });

  const prunedProps = filterPropsUsedByActions({
    actions: prunedData.actions,
    thresholds: prunedData.thresholds,
    workflowProps: {
      ...snippet.props,
      services: indexServicesByInternalName(snippet.props.services),
    },
  });

  return createSnippetVersion({
    ...prunedData,
    ...prunedProps,
    metadata: snippet.metadata,
  });
}

/** Scrubs out secrets from the snippet */
export function scrubSnippetVersion(snippet: SnippetVersion) {
  const indexedServices = indexServicesByInternalName(snippet.props.services);

  const {
    actionsWithNoCredentials,
    impactedServiceActions,
  } = scrubServiceCredentials({
    actions: structuredClone(snippet.data.actions),
    services: indexedServices,
  });

  const scrubbedActions = scrubServiceActions({
    actions: actionsWithNoCredentials,
    services: indexedServices,
  });

  return {
    impactedServiceActions,
    snippet: createSnippetVersion({
      ...snippet.data,
      ...snippet.props,
      actions: scrubbedActions,
      metadata: snippet.metadata,
    }),
  };
}

/**
 * Returns a copy of the snippet with updated account primitives and their corresponding actions.
 * In essence, it runs the snippet through a pipeline with the following operations:
 *  - rename all props and actions that depend on them to resolve any collisions with the account
 *  - flag all ephemeral props and their corresponding actions
 *  - remap service attributes and reason codes to the account
 *  - remaps published attributes
 *  - sorts the incoming outcomes and wf router indices to be incrementally larger than the workflow's
 *
 *
 * For example:
 * If a snippet has a tag prop & tag action with name 'Approved' and the account
 * contains a tag with the same name but different configuration (no equivalency collision),
 * the tag in the snippet is renamed, and any snippet tag actions that point to it receive the
 * updated name.
 *
 * If a snippet has an outcome prop of "Suspended" and it does not exist in the account, the
 * outcome prop & action are flagged as ephemeral.
 */
export function contextualizeSnippetForTargetWorkflow(args: {
  snippet: SnippetVersion;
  workflowPrimitives: {
    actions: Record<Action['publicId'], Action>;
  };
  accountPrimitives: {
    tags: Tag[];
    outcomes: Outcome[];
    services: ServicesByName;
    publishedAttributes: PublishedAttribute[];
    reasonCodeGroups: ReasonCodeGroup[];
    reasonCodes: ReasonCode[];
    lists: List[];
  };
}): SnippetVersion {
  const { snippet, accountPrimitives, workflowPrimitives } = args;

  const collisionsWithinAccountPrimitives = getCollisionsWithinPrimitives({
    primitives: buildPrimitives({
      actions: workflowPrimitives.actions,
      services: accountPrimitives.services,
      tags: accountPrimitives.tags,
      outcomes: accountPrimitives.outcomes,
      publishedAttributes: accountPrimitives.publishedAttributes,
      reasonCodeGroups: accountPrimitives.reasonCodeGroups,
      lists: accountPrimitives.lists,
    }),
    context: {
      workflowData: {
        services: accountPrimitives.services,
      },
    },
  });


  // Snippet with renamed primitive props to resolve non-equivalent name collisions
  const snippetWithRenamedPrimitives = resolveNameCollisions({
    accountCollisionMap: collisionsWithinAccountPrimitives,
    accountPrimitives: {
      tags: accountPrimitives.tags,
      outcomes: accountPrimitives.outcomes,
      publishedAttributes: accountPrimitives.publishedAttributes,
      reasonCodeGroups: accountPrimitives.reasonCodeGroups,
      lists: accountPrimitives.lists,
    },
    snippet,
  });


  // Snippet with ephemeral tag & outcome props, as well as tag & outcome actions
  const snippetWithFlaggedEphemeralPrimitives = flagEphemeralPrimitives({
    accountCollisionMap: collisionsWithinAccountPrimitives,
    snippet: snippetWithRenamedPrimitives,
  });


  /**
   * Snippet with updated thresholds & data routes to use the service attributes from the target
   * account's environment
  */
  const snippetWithResolvedServiceAttributes = resolveServiceAttributes({
    snippet: snippetWithFlaggedEphemeralPrimitives,
    accountCollisionMap: collisionsWithinAccountPrimitives,
    accountPrimitives: {
      services: Object.values(accountPrimitives.services),
    },
  });


  // Snippet with updated published attribute tokens in both props and actions
  const snippetWithResolvedPublishedAttributes = resolvePublishedAttributes({
    snippet: snippetWithResolvedServiceAttributes,
    accountCollisionMap: collisionsWithinAccountPrimitives,
  });


  // Snippet with RCGs from the account instead of the snippets, if they have a collision
  const snippetWithResolvedReasonCodeGroups = resolveReasonCodeGroups({
    snippet: snippetWithResolvedPublishedAttributes,
    accountCollisionMap: collisionsWithinAccountPrimitives,
    accountPrimitives: {
      reasonCodeGroups: accountPrimitives.reasonCodeGroups,
      reasonCodes: accountPrimitives.reasonCodes,
    },
  });


  // Snippet with the correct precedence indexes for indexed actions (outcomes and workflow routers)
  const snippetWithResolvedPrecedenceIndexedActions = resolvePrecedenceIndexedActions({
    snippet: snippetWithResolvedReasonCodeGroups,
    accountCollisionMap: collisionsWithinAccountPrimitives,
    workflowPrimitives: {
      actions: workflowPrimitives.actions,
    },
  });

  const snippetWithResolvedLists = resolveLists({
    snippet: snippetWithResolvedPrecedenceIndexedActions,
    accountCollisionMap: collisionsWithinAccountPrimitives,
  });


  return snippetWithResolvedLists;
}


/**
 * Builds a Map containing the name of a prop and the name it should be renamed to.
 * @example
 *  ["tag-a", "tag-a (1)"]
 *  ["approved", "approved (1)"]
 *  ["denied (1)", "denied (2)"]
 */
function buildPropRenameMap<
  P extends Outcome | Tag | PublishedAttribute | ReasonCodeGroup | List,
>(args: {
  accountProps: P[];
  snippetProps: P[];
  accountCollisionMap: Map<EquivalencyKey, Set<string>>;
  // function to extract the name from the prop (changes depending on what prop P is passed)
  nameExtractor: (primitive: P) => string;
}): Map<string, string> {
  const {
    accountProps,
    snippetProps,
    accountCollisionMap,
    nameExtractor,
  } = args;


  /**
   * it's possible that the next name of a snippet prop collides with the name of another
   * snippet prop. for example, if the snippet contains tags: 'test' and 'test (1)'. the next
   * name for 'test' should be 'test (2)', as 'test (1' is already taken by another snippet prop.
   * hence, we need to check in both the account and snippet props.
  */
  const uniqueAccountPropNames = new Set(accountProps.map(nameExtractor));
  const uniqueSnippetPropNames = new Set(snippetProps.map(nameExtractor));
  const uniqueSnippetAndAccountPropNames = new Set([
    ...uniqueAccountPropNames,
    ...uniqueSnippetPropNames,
  ]);

  const snippetPropsWithoutAccountEquivalent = snippetProps.filter(snippetProp => {
    const snippetPropEquivalencyKey = getEquivalencyKeyForPrimitive({
      primitive: snippetProp,
    });
    return !accountCollisionMap.has(snippetPropEquivalencyKey);
  });

  // no equivalency, but the names happen to be the same
  const snippetPropsWithAccountNameCollisions = (
    snippetPropsWithoutAccountEquivalent.filter(snippetProp => {
      const snippetPropName = nameExtractor(snippetProp);
      return hasNameCollision(snippetPropName, uniqueAccountPropNames);
    })
  );


  const nextPropNameMap = new Map<string, string>();
  snippetPropsWithAccountNameCollisions.forEach(snippetProp => {
    const currentSnippetPropName = nameExtractor(snippetProp);
    const nextSnippetPropName = (
      computeNextName(currentSnippetPropName, uniqueSnippetAndAccountPropNames)
    );

    nextPropNameMap.set(currentSnippetPropName, nextSnippetPropName);
  });


  return nextPropNameMap;
}


/**
 * Returns a copy of a snippet with renamed primitives to resolve collisions with
 * the account.
 */
function resolveNameCollisions(args: {
  snippet: SnippetVersion;
  accountPrimitives: {
    tags: Tag[];
    outcomes: Outcome[];
    publishedAttributes: PublishedAttribute[];
    reasonCodeGroups: ReasonCodeGroup[];
    lists: List[];
  };
  accountCollisionMap: Map<string, Set<string>>;
}): SnippetVersion {
  const {
    snippet,
    accountCollisionMap,
    accountPrimitives,
  } = args;

  // Map containing all snippet tag props that need to be renamed, along with their new names
  const tagRenameMap = buildPropRenameMap({
    accountCollisionMap,
    accountProps: accountPrimitives.tags,
    nameExtractor: tag => tag.tag_name,
    snippetProps: snippet.props.tags,
  });


  // Map containing all snippet outcome props that need to be renamed, along with their new names
  const outcomeRenameMap = buildPropRenameMap({
    accountCollisionMap,
    accountProps: accountPrimitives.outcomes,
    nameExtractor: outcome => outcome.outcome,
    snippetProps: snippet.props.outcomes,
  });


  // Map containing all snippet published attribute props that need to be renamed, along with their new names
  const publishedAttributeRenameMap = buildPropRenameMap({
    accountCollisionMap,
    accountProps: accountPrimitives.publishedAttributes,
    nameExtractor: publishedAttribute => publishedAttribute.attributeName,
    snippetProps: snippet.props.publishedAttributes,
  });


  // Map containing all snippet reason code group props that need to be renamed, along with their new names
  const reasonCodeGroupRenameMap = buildPropRenameMap({
    accountCollisionMap,
    accountProps: accountPrimitives.reasonCodeGroups,
    nameExtractor: reasonCodeGroup => reasonCodeGroup.name,
    snippetProps: snippet.props.reasonCodeGroups,
  });

  const listsRenameMap = buildPropRenameMap({
    accountCollisionMap,
    accountProps: accountPrimitives.lists,
    nameExtractor: list => list.list_name,
    snippetProps: snippet.props.lists,
  });

  const renamedSnippetTagProps = snippet.props.tags.map(snippetTagProp => {
    const currentSnippetTagPropName = snippetTagProp.tag_name;

    if (!tagRenameMap.has(currentSnippetTagPropName)) {
      return snippetTagProp;
    }
    return {
      ...snippetTagProp,
      tag_name: tagRenameMap.get(currentSnippetTagPropName),
    };
  });

  const renamedSnippetOutcomeProps = snippet.props.outcomes.map(snippetOutcomeProp => {
    const currentSnippetOutcomePropName = snippetOutcomeProp.outcome;

    if (!outcomeRenameMap.has(currentSnippetOutcomePropName)) {
      return snippetOutcomeProp;
    }
    return {
      ...snippetOutcomeProp,
      outcome: outcomeRenameMap.get(currentSnippetOutcomePropName),
    };
  });

  const renamedPublishedAttributes = snippet
    .props.publishedAttributes.map(snippetPublishedAttribute => {
      const currentPublishedAttributeName = snippetPublishedAttribute.attributeName;
      if (!publishedAttributeRenameMap.has(currentPublishedAttributeName)) {
        return snippetPublishedAttribute;
      }

      return {
        ...snippetPublishedAttribute,
        attributeName: publishedAttributeRenameMap.get(currentPublishedAttributeName),
      };
    });

  const renamedSnippetReasonCodeGroups = snippet
    .props.reasonCodeGroups.map(snippetReasonCodeGroup => {
      const currentSnippetReasonCodeGroupName = snippetReasonCodeGroup.name;
      if (!reasonCodeGroupRenameMap.has(currentSnippetReasonCodeGroupName)) {
        return snippetReasonCodeGroup;
      }
      return {
        ...snippetReasonCodeGroup,
        name: reasonCodeGroupRenameMap.get(currentSnippetReasonCodeGroupName),
      };
    });

  const renamedSnippetListProps = snippet.props.lists.map(snippetList => {
    const currentSnippetListName = snippetList.list_name;
    if (!listsRenameMap.has(currentSnippetListName)) {
      return snippetList;
    }
    return {
      ...snippetList,
      list_name: listsRenameMap.get(currentSnippetListName),
    };
  });

  const renamedSnippetActions = Object.values(snippet.data.actions).map(snippetAction => {
    if (isActionOfType(snippetAction, ActionType.TAG)) {
      const currentSnippetTagActionName = getActionName(snippetAction);
      if (tagRenameMap.has(currentSnippetTagActionName)) {
        const nextTagPropName = tagRenameMap.get(currentSnippetTagActionName);
        return setActionName(snippetAction, nextTagPropName);
      }
    }

    if (isActionOfType(snippetAction, ActionType.OUTCOME)) {
      const currentSnippetOutcomeActionName = getActionName(snippetAction);
      if (outcomeRenameMap.has(currentSnippetOutcomeActionName)) {
        const nextOutcomePropName = outcomeRenameMap.get(currentSnippetOutcomeActionName);
        return setActionName(snippetAction, nextOutcomePropName);
      }
    }


    // handles INPUT and OUTPUT actions
    if (isPublishedAttribute(snippetAction)) {
      const currentSnippetAttributeActionName = getPublishedDetails(snippetAction)?.attributeName;
      if (publishedAttributeRenameMap.has(currentSnippetAttributeActionName)) {
        const nextPublishedAttributePropName = (
          publishedAttributeRenameMap.get(currentSnippetAttributeActionName)
        );
        return setActionName(snippetAction, nextPublishedAttributePropName);
      }
    }

    if (isListAction(snippetAction)) {
      const currentSnippetListActionName = getActionName(snippetAction);
      if (listsRenameMap.has(currentSnippetListActionName)) {
        const nextListPropName = listsRenameMap.get(currentSnippetListActionName);
        return setActionName(snippetAction, nextListPropName);
      }
    }
    return snippetAction;
  });

  const renamedSnippetThresholds = Object.values(snippet.data.thresholds).map(snippetThreshold => {
    if (snippetThreshold.right?.reasonCodeGroup) {
      const currentSnippetRCGName = snippetThreshold.right.reasonCodeGroup.name;

      if (reasonCodeGroupRenameMap.has(currentSnippetRCGName)) {
        const nextRCGName = reasonCodeGroupRenameMap.get(currentSnippetRCGName);
        return {
          ...snippetThreshold,
          right: {
            ...snippetThreshold.right,
            reasonCodeGroup: {
              ...snippetThreshold.right.reasonCodeGroup,
              name: nextRCGName,
            },
          },
        };
      }
    }

    return snippetThreshold;
  });


  return createSnippetVersion({
    actions: indexActionsByPublicId(renamedSnippetActions),
    dataRoutes: snippet.data.dataRoutes,
    thresholds: indexThresholdsByPublicId(renamedSnippetThresholds),
    outcomes: renamedSnippetOutcomeProps,
    publishedAttributes: renamedPublishedAttributes,
    reasonCodeGroups: renamedSnippetReasonCodeGroups,
    services: snippet.props.services,
    tags: renamedSnippetTagProps,
    lists: renamedSnippetListProps,
    metadata: snippet.metadata,
  });
}


/**
 * Returns a copy of a Snippet with primitives marked as ephemeral if they
 * do not exist in the account
 */
function flagEphemeralPrimitives(args: {
  snippet: SnippetVersion;
  accountCollisionMap: Map<string, Set<string>>;
}): SnippetVersion {
  const { snippet, accountCollisionMap } = args;

  const flaggedTagProps = snippet.props.tags.map(snippetTagProp => {
    const snippetTagPropEquivalencyKey = getEquivalencyKeyForPrimitive({
      primitive: snippetTagProp,
    });

    return {
      ...snippetTagProp,
      is_ephemeral: !accountCollisionMap.has(snippetTagPropEquivalencyKey),
    };
  });

  const flaggedOutcomeProps = snippet.props.outcomes.map(snippetOutcomeProp => {
    const snippetOutcomePropEquivalencyKey = getEquivalencyKeyForPrimitive({
      primitive: snippetOutcomeProp,
    });
    return {
      ...snippetOutcomeProp,
      is_ephemeral: !accountCollisionMap.has(snippetOutcomePropEquivalencyKey),
    };
  });

  const flaggedPublishedAttributeProps = snippet.props.publishedAttributes
    .map(snippetPublishedAttribute => {
      const snippetPublishedAttributeEquivalencyKey = getEquivalencyKeyForPrimitive({
        primitive: snippetPublishedAttribute,
      });

      return {
        ...snippetPublishedAttribute,
        isEphemeral: !accountCollisionMap.has(snippetPublishedAttributeEquivalencyKey),
      };
    });

  const flaggedReasonCodeGroupProps = snippet.props.reasonCodeGroups.map(snippetRCG => {
    const snippetRCGEquivalencyKey = getEquivalencyKeyForPrimitive({
      primitive: snippetRCG,
    });

    return {
      ...snippetRCG,
      isEphemeral: !accountCollisionMap.has(snippetRCGEquivalencyKey),
    };
  });

  const flaggedListProps = snippet.props.lists.map(snippetList => {
    const snippetListEquivalencyKey = getEquivalencyKeyForPrimitive({
      primitive: snippetList,
    });

    return {
      ...snippetList,
      is_ephemeral: !accountCollisionMap.has(snippetListEquivalencyKey),
    };
  });

  // Flag attributes within services as ephemeral
  const servicesWithFlaggedAttributes = snippet.props.services.map(service => {
    const flaggedAttributes = Object.fromEntries(
      Object.entries(service.attributes ?? {}).map(([attrId, attribute]) => {
        const attributeEquivalencyKey = getEquivalencyKeyForPrimitive({
          primitive: {
            attribute,
            serviceInternalName: service.internal_name,
          },
        });
        return [
          attrId,
          {
            ...attribute,
            isEphemeral: !accountCollisionMap.has(attributeEquivalencyKey),
          },
        ];
      }),
    );

    return {
      ...service,
      attributes: flaggedAttributes,
    };
  });

  const flaggedSnippetTagsByName = indexTagsByName(flaggedTagProps);
  const flaggedSnippetOutcomesByName = indexOutcomesByName(flaggedOutcomeProps);
  const flaggedSnippetPAsByName = indexPublishedAttributesByName(flaggedPublishedAttributeProps);
  const flaggedSnippetRCGsByName = indexReasonCodeGroupsByName(flaggedReasonCodeGroupProps);
  const flaggedSnippetListsByName = indexListsByName(flaggedListProps);

  const flaggedActions = Object.values(snippet.data.actions).map(snippetAction => {
    if (isActionOfType(snippetAction, ActionType.TAG)) {
      const snippetTagActionName = getActionName(snippetAction);
      const snippetTagPropForAction = flaggedSnippetTagsByName[snippetTagActionName];
      return {
        ...snippetAction,
        tag: {
          ...snippetAction.tag,
          isEphemeral: Boolean(snippetTagPropForAction.is_ephemeral),
        },
      };
    }
    if (isActionOfType(snippetAction, ActionType.OUTCOME)) {
      const snippetOutcomeActionName = getActionName(snippetAction);
      const snippetOutcomePropForAction = flaggedSnippetOutcomesByName[snippetOutcomeActionName];
      return {
        ...snippetAction,
        outcome: {
          ...snippetAction.outcome,
          isEphemeral: Boolean(snippetOutcomePropForAction.is_ephemeral),
        },
      };
    }

    if (isPublishedAttribute(snippetAction)) {
      const snippetAttributeActionName = getPublishedDetails(snippetAction)?.attributeName;

      const snippetPublishedAttributePropForAction = (
        flaggedSnippetPAsByName[snippetAttributeActionName]
      );

      if (isActionOfType(snippetAction, ActionType.INPUT)) {
        return {
          ...snippetAction,
          input: {
            ...snippetAction.input,
            publishedDetails: {
              ...snippetAction.input.publishedDetails,
              isEphemeral: Boolean(snippetPublishedAttributePropForAction?.isEphemeral),
            },
          },
        };
      }

      if (isActionOfType(snippetAction, ActionType.OUTPUT)) {
        return {
          ...snippetAction,
          output: {
            ...snippetAction.output,
            publishedDetails: {
              ...snippetAction.output.publishedDetails,
              isEphemeral: Boolean(snippetPublishedAttributePropForAction?.isEphemeral),
            },
          },
        };
      }
    }

    if (isListAction(snippetAction)) {
      const snippetListActionName = getActionName(snippetAction);
      const snippetListPropForAction = flaggedSnippetListsByName[snippetListActionName];
      return {
        ...snippetAction,
        service: {
          ...snippetAction.service,
          isListEphemeral: isListEphemeral(snippetListPropForAction),
        },
      };
    }

    return snippetAction;
  });

  const flaggedThresholds = Object.values(snippet.data.thresholds).map(snippetThreshold => {
    const thresholdRCG = snippetThreshold.right?.reasonCodeGroup;
    if (thresholdRCG) {
      const snippetRCGPropForThreshold = flaggedSnippetRCGsByName[thresholdRCG.name];

      if (snippetRCGPropForThreshold) {
        return {
          ...snippetThreshold,
          right: {
            ...snippetThreshold.right,
            reasonCodeGroup: {
              ...snippetThreshold.right?.reasonCodeGroup,
              isEphemeral: Boolean(snippetRCGPropForThreshold.isEphemeral),
            },
          },
        };
      }
    }

    return snippetThreshold;
  });


  return createSnippetVersion({
    actions: indexActionsByPublicId(flaggedActions),
    dataRoutes: snippet.data.dataRoutes,
    thresholds: indexThresholdsByPublicId(flaggedThresholds),
    outcomes: flaggedOutcomeProps,
    services: servicesWithFlaggedAttributes,
    tags: flaggedTagProps,
    publishedAttributes: flaggedPublishedAttributeProps,
    reasonCodeGroups: flaggedReasonCodeGroupProps,
    lists: flaggedListProps,
    metadata: snippet.metadata,
  });
}


/**
 * Resolves an attribute to its final form: for attributes that collide with
 * account-level attributes, returns the account's canonical attribute;
 * otherwise returns the attribute with an existing or newly assigned ephemeral ID.
 */
const getResolvedAttribute = (args: {
  attribute: Attribute;
  serviceInternalName: string;
  accountCollisionMap: Map<EquivalencyKey, Set<string>>;
  accountAttrsByEquivalencyKey: Record<string, Attribute>;
  ephemeralIdAssigner: ReturnType<typeof createEphemeralAttributeIdAssigner>;
}): Attribute => {
  const {
    attribute,
    serviceInternalName,
    accountCollisionMap,
    accountAttrsByEquivalencyKey,
    ephemeralIdAssigner,
  } = args;

  const equivalencyKey = getEquivalencyKeyForPrimitive({
    primitive: { attribute, serviceInternalName },
  });
  if (!accountCollisionMap.has(equivalencyKey)) {
    const existingEphemeralId = ephemeralIdAssigner.getEphemeralAttributeId(equivalencyKey);
    if (existingEphemeralId) {
      return {
        ...attribute,
        attribute_id: existingEphemeralId,
      };
    }
    return {
      ...attribute,
      attribute_id: ephemeralIdAssigner.assignEphemeralAttributeId(equivalencyKey),
    };
  }
  return accountAttrsByEquivalencyKey[equivalencyKey];
};


/**
 * Returns a copy of the Snippet with updated thresholds & data routes remapping any attribute
 * data in them to the service attributes defined in the target account (where the workflow lives).
 * Important assumptions and context:
 *  - all environments have the same Services
 *  - Attributes across environments do **not** have the same tokens across environments, but their
 *    selectors are consistent.
 *  - selectors are not unique within all services.
 */
function resolveServiceAttributes(args: {
  snippet: SnippetVersion;
  accountCollisionMap: Map<EquivalencyKey, Set<string>>;
  accountPrimitives: {
    services: ServiceObject[];
  };
}): SnippetVersion {
  const { snippet, accountCollisionMap, accountPrimitives } = args;

  // indices for constant-time lookup
  const snippetAttrs = getAttributesForServices(snippet.props.services);
  const snippetAttrsByToken = indexAttributesByToken(snippetAttrs);
  const snippetServiceInternalNameByAttrToken = indexServiceInternalNamesByAttributeToken(
    snippet.props.services,
  );
  const accountAttrsByEquivalencyKey = indexAttributesByEquivalencyKey(
    accountPrimitives.services,
  );

  // ephemeral attribute ID handling
  const highestAccountAttributeId = getHighestAttributeId(accountPrimitives.services);
  const ephemeralIdAssigner = createEphemeralAttributeIdAssigner(highestAccountAttributeId);

  const resolvedThresholds = Object.values(snippet.data.thresholds).map(threshold => {
    const hasLeftAttr = (
      Boolean(threshold.left.attributeId) && Boolean(threshold.left.attributeToken)
    );

    const hasRightAttr = (
      Boolean(threshold.right?.attributeId) && Boolean(threshold.right?.attributeToken)
    );

    if (!hasLeftAttr && !hasRightAttr) {
      return threshold;
    }

    const resolvedThreshold = structuredClone(threshold);

    if (hasLeftAttr) {
      const attrToken = threshold.left.attributeToken;
      const attribute = snippetAttrsByToken[attrToken];
      const resolvedAttribute = getResolvedAttribute({
        attribute,
        serviceInternalName: snippetServiceInternalNameByAttrToken[attrToken],
        accountCollisionMap,
        accountAttrsByEquivalencyKey,
        ephemeralIdAssigner,
      });
      resolvedThreshold.left = {
        ...resolvedThreshold.left,
        attributeId: resolvedAttribute.attribute_id,
        attributeToken: resolvedAttribute.attribute_token,
      };
    }

    if (hasRightAttr) {
      const attrToken = threshold.right.attributeToken;
      const attribute = snippetAttrsByToken[attrToken];
      const resolvedAttribute = getResolvedAttribute({
        attribute,
        serviceInternalName: snippetServiceInternalNameByAttrToken[attrToken],
        accountCollisionMap,
        accountAttrsByEquivalencyKey,
        ephemeralIdAssigner,
      });
      resolvedThreshold.right = {
        ...resolvedThreshold.right,
        attributeId: resolvedAttribute.attribute_id,
        attributeToken: resolvedAttribute.attribute_token,
      };
    }

    return resolvedThreshold;
  });

  const resolvedDataRoutes = Object.values(snippet.data.dataRoutes).map(dataRoute => {
    const hasAttr = Boolean(dataRoute.attribute.id) && Boolean(dataRoute.attribute.token);

    if (hasAttr) {
      const attrToken = dataRoute.attribute.token;
      const attribute = snippetAttrsByToken[attrToken];
      const resolvedAttribute = getResolvedAttribute({
        attribute,
        serviceInternalName: snippetServiceInternalNameByAttrToken[attrToken],
        accountCollisionMap,
        accountAttrsByEquivalencyKey,
        ephemeralIdAssigner,
      });
      return {
        ...dataRoute,
        attribute: {
          ...dataRoute.attribute,
          attributeName: resolvedAttribute.attribute_name,
          id: resolvedAttribute.attribute_id,
          token: resolvedAttribute.attribute_token,
        },
      };
    }

    return dataRoute;
  });

  // Resolve each service's attributes: for snippet-only attributes that were assigned
  // ephemeral IDs (when resolving thresholds/data routes)
  const resolvedServices = (snippet.props.services ?? []).map(service => {
    const attrs = service?.attributes ?? {};
    const internalName = service.internal_name;
    const resolvedAttrs = Object.fromEntries(
      Object.entries(attrs).map(([key, attr]) => {
        const equivalencyKey = getEquivalencyKeyForPrimitive({
          primitive: { attribute: attr, serviceInternalName: internalName },
        });
        const attrId = ephemeralIdAssigner.getEphemeralAttributeId(equivalencyKey) ?? Number(key);
        const resolved = { ...attr, attribute_id: attrId };
        return [attrId, resolved];
      }),
    ) as ServiceObject['attributes'];
    return { ...service, attributes: resolvedAttrs };
  });

  return createSnippetVersion({
    actions: snippet.data.actions,
    dataRoutes: indexDataRoutesByPublicId(resolvedDataRoutes),
    thresholds: indexThresholdsByPublicId(resolvedThresholds),
    outcomes: snippet.props.outcomes,
    services: resolvedServices,
    tags: snippet.props.tags,
    publishedAttributes: snippet.props.publishedAttributes,
    reasonCodeGroups: snippet.props.reasonCodeGroups,
    lists: snippet.props.lists,
    metadata: snippet.metadata,
  });
}


/**
 * Returns a copy of the Snippet with updated published attribute tokens in both props and actions.
 * Resolving means that if a published attribute token is not found in the account, a temporary token is generated,
 * and the PA prop & details within an action are updated to use the temporary token.
 */
function resolvePublishedAttributes(args: {
  snippet: SnippetVersion;
  accountCollisionMap: Map<EquivalencyKey, Set<string>>;
}): SnippetVersion {
  const { snippet, accountCollisionMap } = args;

  const resolvedSnippetPublishedAttributeProps = snippet
    .props.publishedAttributes
    .map(snippetPublishedAttribute => {
      const snippetPublishedAttributeEquivalencyKey = getEquivalencyKeyForPrimitive({
        primitive: snippetPublishedAttribute,
      });

      if (!accountCollisionMap.has(snippetPublishedAttributeEquivalencyKey)) {
        /**
         * the PA token is set by the backend, but the FE needs to have a consistent token
         * across PA props and its Actions
         */
        const temporaryPublishedAttributeToken = generateTemporaryPublishedAttributeToken();
        return {
          ...snippetPublishedAttribute,
          attributeToken: temporaryPublishedAttributeToken,
        };
      }

      return snippetPublishedAttribute;
    });

  const resolvedSnippetPublishedAttributesByName = indexPublishedAttributesByName(
    resolvedSnippetPublishedAttributeProps,
  );

  const resolvedActions = Object.values(snippet.data.actions).map(snippetAction => {
    // we only need to resolve PA snippet actions
    if (!isPublishedAttribute(snippetAction)) {
      return snippetAction;
    }

    const snippetAttributeAction = getPublishedDetails(snippetAction)?.attributeName;
    const resolvedPublishedAttributePropForAction = resolvedSnippetPublishedAttributesByName[
      snippetAttributeAction
    ];
    if (!resolvedPublishedAttributePropForAction) {
      return snippetAction;
    }

    const resolvedAttributeToken = resolvedPublishedAttributePropForAction.attributeToken;

    if (isActionOfType(snippetAction, ActionType.INPUT)) {
      return {
        ...snippetAction,
        input: {
          ...snippetAction.input,
          publishedAttributeToken: resolvedAttributeToken,
          publishedDetails: {
            ...snippetAction.input.publishedDetails,
            attributeToken: resolvedAttributeToken,
          },
        },
      };
    }
    if (isActionOfType(snippetAction, ActionType.OUTPUT)) {
      return {
        ...snippetAction,
        output: {
          ...snippetAction.output,
          publishedAttributeToken: resolvedAttributeToken,
          publishedDetails: {
            ...snippetAction.output.publishedDetails,
            attributeToken: resolvedAttributeToken,
          },
        },
      };
    }

    // should not get here as all non-input/output actions are covered by early return
    return snippetAction;
  });


  return createSnippetVersion({
    actions: indexActionsByPublicId(resolvedActions),
    thresholds: snippet.data.thresholds,
    dataRoutes: snippet.data.dataRoutes,
    tags: snippet.props.tags,
    outcomes: snippet.props.outcomes,
    services: snippet.props.services,
    publishedAttributes: resolvedSnippetPublishedAttributeProps,
    reasonCodeGroups: snippet.props.reasonCodeGroups,
    lists: snippet.props.lists,
    metadata: snippet.metadata,
  });
}


/**
 * returns a copy of the snippet with its ReasonCodeGroups (RCG) properties updated such that:
 * - if a ReasonCodeGroup in the snippet collides (is equivalent) with one from the account, the account's RCG is used instead.
 * - any reference to a ReasonCodeGroup in a threshold (right.reasonCodeGroup) is also updated to the account's if a collision exists.
 * - otherwise, if there is no collision, the snippet RCG is kept, but its reasonCodes are remapped to account reasonCodes via their unique service/code keys.
 *
 * this function ensures that RCGs from account data take precedence and are reused in the snippet wherever possible.
 */
function resolveReasonCodeGroups(args: {
  snippet: SnippetVersion;
  accountCollisionMap: Map<EquivalencyKey, Set<string>>;
  accountPrimitives: {
    reasonCodeGroups: ReasonCodeGroup[];
    reasonCodes: ReasonCode[];
  };
}): SnippetVersion {
  const { snippet, accountPrimitives, accountCollisionMap } = args;

  // pre-compute indices as they are used in more than one loop
  const accountRCGsByName = indexReasonCodeGroupsByName(accountPrimitives.reasonCodeGroups);
  const accountRCsByServiceCodeKey = indexReasonCodesByServiceCodeKey(
    accountPrimitives.reasonCodes,
  );

  // the equivalencyKey for RCGs has a nested loop so they are computed upfront
  const equivalencyKeysForSnippetRCGByName = indexPrimitiveEquivalencyKeysByIdentifier({
    identifierExtractor: reasonCodeGroup => reasonCodeGroup.name,
    primitives: snippet.props.reasonCodeGroups,
  });

  /**
   * RCGs resolved to either:
   * - use the RCG from the account (if there's a collision),
   * - or update the RCs inside of the snippet RCG to match the account RCs.
   *
   * note: if a RCG does not have a collision (is ephemeral), an `id` must be given to it
   * that is the next available one
   */
  let ephemeralRCGCount = 0;
  const highestReasonCodeGroupId = getHighestReasonCodeGroupId(accountPrimitives.reasonCodeGroups);

  const resolvedReasonCodeGroups = snippet.props.reasonCodeGroups.map(snippetRCG => {
    const snippetRCGEquivalencyKey = equivalencyKeysForSnippetRCGByName[snippetRCG.name];
    const hasCollision = accountCollisionMap.has(snippetRCGEquivalencyKey);

    if (hasCollision) {
      return accountRCGsByName[snippetRCG.name];
    }

    // keep a separate counter because the accountPrimitives.reasonCodeGroups length wont change until insertion
    ephemeralRCGCount += 1;
    return {
      ...snippetRCG,
      id: highestReasonCodeGroupId + ephemeralRCGCount,
      reasonCodes: snippetRCG.reasonCodes.flatMap(snippetReasonCode => {
        // ignore numeric reason codes
        if (typeof snippetReasonCode === 'number') {
          return [];
        }
        const reasonCodeServiceCodeKey = createReasonCodeServiceCodeKey(snippetReasonCode);
        const accountReasonCode = accountRCsByServiceCodeKey[reasonCodeServiceCodeKey];
        // the accountReasonCode should always be found, but providing a fallback just in case
        return accountReasonCode ?? snippetReasonCode;
      }),
    };
  });


  // index the resolved RCGs so we can use them for thresholds
  const snippetResolvedRCGsByName = indexReasonCodeGroupsByName(resolvedReasonCodeGroups);

  /**
   * resolved the threshold RCGs to either:
   * - use the RCG in the account
   * - use the resolved snippet RCG from the step above
   */
  const resolvedThresholds = Object.values(snippet.data.thresholds).map(snippetThreshold => {
    // do not resolve thresholds that have no RCGs in them
    if (!snippetThreshold.right?.reasonCodeGroup) {
      return snippetThreshold;
    }

    const thresholdRCG = snippetThreshold.right.reasonCodeGroup;
    const thresholdRCGEquivalencyKey = equivalencyKeysForSnippetRCGByName[thresholdRCG.name];
    const hasCollision = accountCollisionMap.has(thresholdRCGEquivalencyKey);

    const rcgToUse = hasCollision
      ? accountRCGsByName[thresholdRCG.name]
      : snippetResolvedRCGsByName[thresholdRCG.name];

    return {
      ...snippetThreshold,
      right: {
        ...snippetThreshold.right,
        reasonCodeGroup: rcgToUse,
        reasonCodeGroupId: rcgToUse.id,
      },
    };
  });

  return createSnippetVersion({
    actions: snippet.data.actions,
    dataRoutes: snippet.data.dataRoutes,
    thresholds: indexThresholdsByPublicId(resolvedThresholds),
    outcomes: snippet.props.outcomes,
    publishedAttributes: snippet.props.publishedAttributes,
    services: snippet.props.services,
    tags: snippet.props.tags,
    reasonCodeGroups: resolvedReasonCodeGroups,
    lists: snippet.props.lists,
    metadata: snippet.metadata,
  });
}
/**
 * returns a copy of the snippet with its list props + actions updated such that:
 * - If a list is not equivalent with one from the account, a temporary list token is generated for the prop
 *  and that token is used as the value for the `customer_list_id` option
 * This prevents id collisions if c/p-ing between cells and ensures that we are using a temporary value
 */
function resolveLists(args: {
  snippet: SnippetVersion;
  accountCollisionMap: Map<EquivalencyKey, Set<string>>;
}): SnippetVersion {
  const { snippet, accountCollisionMap } = args;

  const resolvedSnippetListProps = snippet.props.lists.map(snippetList => {
    const snippetListEquivalencyKey = getEquivalencyKeyForPrimitive({ primitive: snippetList });
    if (!accountCollisionMap.has(snippetListEquivalencyKey)) {
      // lists have a token, but use customer_list_id as the identifier
      // to prevent accidental collisions, we are using the temporary token as the id
      const temporaryListToken = generateTemporaryListToken();
      return {
        ...snippetList,
        list_token: temporaryListToken,
        customer_list_id: temporaryListToken,
      };
    }
    return snippetList;
  });

  const resolvedSnippetListsByName = indexListsByName(resolvedSnippetListProps);

  const resolvedActions = Object.values(snippet.data.actions).map(snippetAction => {
    if (!isListAction(snippetAction)) {
      return snippetAction;
    }

    const snippetListActionName = getActionName(snippetAction);
    const resolvedListPropForAction = resolvedSnippetListsByName[snippetListActionName];

    if (!resolvedListPropForAction) {
      return snippetAction;
    }

    const resolvedListIdentifier = resolvedListPropForAction.customer_list_id;

    return {
      ...snippetAction,
      options: {
        ...snippetAction.options,
        [CUSTOMER_LIST_ID_OPTION_KEY]: {
          ...snippetAction.options[CUSTOMER_LIST_ID_OPTION_KEY],
          value: resolvedListIdentifier,
        },
      },
    };
  });

  return createSnippetVersion({
    actions: indexActionsByPublicId(resolvedActions),
    thresholds: snippet.data.thresholds,
    dataRoutes: snippet.data.dataRoutes,
    tags: snippet.props.tags,
    outcomes: snippet.props.outcomes,
    services: snippet.props.services,
    publishedAttributes: snippet.props.publishedAttributes,
    reasonCodeGroups: snippet.props.reasonCodeGroups,
    lists: resolvedSnippetListProps,
    metadata: snippet.metadata,
  });
}
/**
 * Returns a copy of the Snippet with resolved precedence indexed actions (Outcomes and Workflow Routers).
 * The precedence index of these actions are resolved to be incrementally and sequentially higher than
 * the one of the workflow, while leaving no gaps in between them.
 * For example:
 *
 * snippet Action indices:
 *    - A(1)
 *    - B(2) (has collision against workflow)
 *    - C(3)
 *
 * workflow max index: 2
 * result:
 *    - A(3) -> next number after workflow max of 2
 *    - B(2) -> has collision, index is not updated
 *    - C(4) -> next number after snippet A
 *
 * note: B is not modified because it will not be inserted by the algorithm later on. although this does create
 * hidden coupling between the contextualization pipeline and the insertion algorithm, it simplifies it by not
 * putting the responsibility of adjusting the indices on it.
 *
 */
function resolvePrecedenceIndexedActions(args: {
  snippet: SnippetVersion;
  accountCollisionMap: Map<EquivalencyKey, Set<string>>;
  workflowPrimitives: {
    actions: Record<Action['publicId'], Action>;
  };
}): SnippetVersion {
  const { snippet, accountCollisionMap, workflowPrimitives } = args;


  const workflowPrecedenceIndexedActions = getPrecedenceIndexedActions(workflowPrimitives.actions);

  // if there are no precedence indexed actions in the WF, there is nothing to resolve in the snippet
  if (workflowPrecedenceIndexedActions.length === 0) {
    return snippet;
  }

  const highestWorkflowActionPrecedence = findHighestExistingActionPrecedence(
    workflowPrecedenceIndexedActions,
  );

  const snippetNonPrecedenceIndexedActions = getNonPrecedenceIndexedActions(snippet.data.actions);
  const snippetPrecedenceIndexedActionsASC = sortActionsByPrecedenceIndex(
    getPrecedenceIndexedActions(snippet.data.actions),
  );


  let skippedActionCounter = 0;
  const resolvedSnippetPrecedenceIndexedActions = snippetPrecedenceIndexedActionsASC
    .map(snippetPrecedenceIndexedAction => {
      // do not modify the default action as it'll be inserted by the insertion algorithm
      if (isDefaultPrecedenceIndexedAction(snippetPrecedenceIndexedAction)) {
        skippedActionCounter += 1;
        return snippetPrecedenceIndexedAction;
      }


      const snippetPIActionEquivalencyKey = getEquivalencyKeyForPrimitive({
        primitive: snippetPrecedenceIndexedAction,
      });

      // do not modify a colliding action, but track that we've skipped it
      const hasCollision = accountCollisionMap.has(snippetPIActionEquivalencyKey);
      if (hasCollision) {
        skippedActionCounter += 1;
        return snippetPrecedenceIndexedAction;
      }

      const snippetActionPrecedenceIndex = getActionPrecedenceIndex(snippetPrecedenceIndexedAction);
      const nextPrecedenceIndex = (
        /**
         * use `highestWorkflowActionPrecedence` as the offset for the incoming `snippetActionPrecedenceIndex`,
         * but subtract the count of skipped actions to remove any gaps between indices
         */
        highestWorkflowActionPrecedence + snippetActionPrecedenceIndex - skippedActionCounter
      );

      return setActionPrecedenceIndex(snippetPrecedenceIndexedAction, nextPrecedenceIndex);
    });


  const resolvedActions = [
    ...snippetNonPrecedenceIndexedActions,
    ...resolvedSnippetPrecedenceIndexedActions,
  ];


  return createSnippetVersion({
    actions: indexActionsByPublicId(resolvedActions),
    dataRoutes: snippet.data.dataRoutes,
    thresholds: snippet.data.thresholds,
    outcomes: snippet.props.outcomes,
    publishedAttributes: snippet.props.publishedAttributes,
    reasonCodeGroups: snippet.props.reasonCodeGroups,
    services: snippet.props.services,
    tags: snippet.props.tags,
    lists: snippet.props.lists,
    metadata: snippet.metadata,
  });
}

/**
 * Returns workflow data where all data unrelated to the action and its upstream ancestors is filtered out.
 * This can be considered a subgraph of the workflow graph.
 */
export function getUpstreamSubgraphFromAction(
  workflowData: WorkflowState['workflowData'],
  actionId: Action['publicId'],
): WorkflowState['workflowData'] {
  const { actions, thresholds, dataRoutes } = workflowData;

  // add all actions that is an incomer to the action identified
  // by the supplied action id using BFS

  const queue: Action[] = [actions[actionId]];
  const subgraphActions: Record<Action['publicId'], Action> = {};

  for (const action of queue) {
    subgraphActions[action.publicId] = action;

    const incomerActions = getIncomerActionsForAction({
      action,
      actions,
      thresholds,
      dataRoutes,
    });

    Object.values(incomerActions).forEach(incomerAction => {
      // only visit each action once
      if (!subgraphActions[incomerAction.publicId]) {
        queue.push(incomerAction);
      }
    });
  }

  // filter all thresholds that do not belong to any of the subgraph actions
  const subgraphThresholds: Record<Threshold['publicId'], Threshold> = {};
  Object.values(thresholds).forEach(threshold => {
    if (subgraphActions[threshold.actionId]) {
      subgraphThresholds[threshold.publicId] = threshold;
    }
  });

  // filter all data routes that are not included in subgraph actions.
  const subgraphDataRoutes: Record<DataRoute['publicId'], DataRoute> = {};
  Object.values(dataRoutes).forEach(dataRoute => {
    if (subgraphActions[dataRoute.targetId]) {
      subgraphDataRoutes[dataRoute.publicId] = dataRoute;
    }
  });

  return {
    ...workflowData,
    actions: subgraphActions,
    thresholds: subgraphThresholds,
    dataRoutes: subgraphDataRoutes,
  };
}


export function indexSnippetsMetadataByToken(
  snippetsMetadata: SnippetMetadata[],
): Record<SnippetMetadata['token'], SnippetMetadata> {
  return Object.fromEntries(
    snippetsMetadata.map(snippetMetadata => [snippetMetadata.token, snippetMetadata]),
  );
}


// ==== linked snippets
export function prepareLinkedSnippetForInsertion(args: {
  snippet: SnippetVersion;
  localToRemotePublicIds: Record<PublicId, PublicId>;
  parentSnippetActionPublicId: PublicId;
}): SnippetVersion {
  /**
   * pending:
   * - handle external actions (might have existing connections to another snippet)
   * todo BUILD-4357: handleExternalActions();
   */
  const { snippet, localToRemotePublicIds, parentSnippetActionPublicId } = args;


  const snippetWithSnippetParentAction = attachParentSnippetAction({
    snippet,
    parentSnippetActionPublicId,
  });

  const snippetWithAttachedChildContainerContexts = attachSnippetChildActionContainerContext({
    localToRemotePublicIds,
    snippet: snippetWithSnippetParentAction,
  });

  const snippetWithGateRules = attachGateRules({
    snippet: snippetWithAttachedChildContainerContexts,
    parentSnippetActionPublicId,
  });

  const sortedSnippet = sortSnippetInTopologicalOrder({
    snippet: snippetWithGateRules,
  });

  return sortedSnippet;
}


export function regenerateLinkedSnippetsPublicIds(args: {
  snippet: SnippetVersion;
  remoteToLocalPublicIdOverrides?: Record<PublicId, PublicId>;
}) {
  const { snippet, remoteToLocalPublicIdOverrides } = args;

  let remoteToLocalPublicIds = generateRandomizedIdPublicIdMap(args.snippet);
  if (remoteToLocalPublicIdOverrides) {
    // override any ids included in the overrides
    remoteToLocalPublicIds = {
      ...remoteToLocalPublicIds,
      ...remoteToLocalPublicIdOverrides,
    };
  }

  const remappedSnippet = remapIds({ data: snippet, idMap: remoteToLocalPublicIds });

  // invert the index
  const localToRemotePublicIds = invertPublicIdMap(remoteToLocalPublicIds);

  return {
    localToRemotePublicIds,
    remoteToLocalPublicIds,
    snippet: remappedSnippet,
  };
}

function attachParentSnippetAction(args: {
  snippet: SnippetVersion;
  parentSnippetActionPublicId: PublicId;
}) {
  const { snippet, parentSnippetActionPublicId } = args;

  if (snippet.metadata === null) {
    throw new Error('Linked Snippet must contain metadata');
  }

  const snippetMetadata = snippet.props.snippets.find(
    s => snippet.metadata.token === s.token,
  );

  const snippetParentAction = createAction({
    type: ActionType.SNIPPET,
    args: {
      name: snippetMetadata?.name,
      publicId: parentSnippetActionPublicId,
      publicVersion: snippet.metadata.version,
      token: snippet.metadata.token,
    },
  });


  const updatedActions = [...Object.values(snippet.data.actions), snippetParentAction];

  return createSnippetVersion({
    actions: indexActionsByPublicId(updatedActions),
    thresholds: snippet.data.thresholds,
    dataRoutes: snippet.data.dataRoutes,
    lists: snippet.props.lists,
    outcomes: snippet.props.outcomes,
    publishedAttributes: snippet.props.publishedAttributes,
    reasonCodeGroups: snippet.props.reasonCodeGroups,
    services: snippet.props.services,
    tags: snippet.props.tags,
    metadata: snippet.metadata,
    snippets: snippet.props.snippets,
  });
}


function attachSnippetChildActionContainerContext(args: {
  snippet: SnippetVersion;
  localToRemotePublicIds: Record<PublicId, PublicId>;
}): SnippetVersion {
  const { snippet, localToRemotePublicIds } = args;


  const snippetActions = Object.values(snippet.data.actions).filter(snippetAction => (
    isActionOfType(snippetAction, ActionType.SNIPPET)
  ));

  const snippetActionCount = snippetActions.length;
  if (snippetActionCount !== 1) {
    throw new Error(`snippet action count (${snippetActionCount}) cannot be different than 1`);
  }

  const [snippetParentAction] = snippetActions;

  const actionsWithContainerContext: Action[] = Object.values(snippet.data.actions)
    .map(snippetAction => {
      // do not update the snippet parent action as it should not have any container context
      if (snippetAction === snippetParentAction) {
        return snippetAction;
      }

      const remoteActionPublicId = localToRemotePublicIds[snippetAction.publicId];
      if (!remoteActionPublicId) {
        throw new Error(`could not resolve remote action public id: ${snippetAction.publicId}`);
      }

      return {
        ...snippetAction,
        containerContext: {
          type: ContainerType.SNIPPET,
          actionOptionPublicId: generatePublicId('actionoption'),
          snippetRemoteActionMap: {
            /**
             * the map contains a link between the snippet parent and the id of this same
             * snippet action _at_ the workflow version related to the snippet parent
             */
            [snippetParentAction.publicId]: remoteActionPublicId,
          },
        },
      };
    });

  return createSnippetVersion({
    actions: indexActionsByPublicId(actionsWithContainerContext),
    thresholds: snippet.data.thresholds,
    dataRoutes: snippet.data.dataRoutes,
    lists: snippet.props.lists,
    outcomes: snippet.props.outcomes,
    publishedAttributes: snippet.props.publishedAttributes,
    reasonCodeGroups: snippet.props.reasonCodeGroups,
    services: snippet.props.services,
    tags: snippet.props.tags,
    metadata: snippet.metadata,
  });
}


export function attachGateRules(args: {
  snippet: SnippetVersion;
  parentSnippetActionPublicId: PublicId;
}) {
  const { snippet, parentSnippetActionPublicId } = args;
  const snippetActions = snippet.data.actions;

  // Create gate rule thresholds for any actions which required them
  const actionsRequiringGateRule = getActionsRequiringGateRule(snippetActions);
  const snippetGateThresholds = actionsRequiringGateRule
    .map(snippetAction => createSnippetGateThreshold({
      parentActionId: parentSnippetActionPublicId,
      ownerActionId: snippetAction.publicId,
    }));

  // Update actions to include new gate rules
  const updatedActionsMap = { ...snippetActions };
  for (const threshold of snippetGateThresholds) {
    const action = updatedActionsMap[threshold.actionId];
    if (action) {
      const mergedRules = addThresholdToBooleanRule({
        thresholdId: threshold.publicId,
        pathToPerformActionAt: booleanRuleStrings.AND_OPERATOR_STRING,
        originalRule: action.rules ?? {},
      });
      if (!mergedRules) throw new Error('Unable to add gate rule');
      updatedActionsMap[threshold.actionId] = { ...action, rules: mergedRules };
    }
  }

  const updatedActions = Object.values(updatedActionsMap);
  const updatedThresholds = [
    ...Object.values(snippet.data.thresholds),
    ...snippetGateThresholds,
  ];

  return createSnippetVersion({
    actions: indexActionsByPublicId(updatedActions),
    thresholds: indexThresholdsByPublicId(updatedThresholds),
    dataRoutes: snippet.data.dataRoutes,
    lists: snippet.props.lists,
    outcomes: snippet.props.outcomes,
    publishedAttributes: snippet.props.publishedAttributes,
    reasonCodeGroups: snippet.props.reasonCodeGroups,
    services: snippet.props.services,
    tags: snippet.props.tags,
    metadata: snippet.metadata,
  });
}

// todo BUILD-4357
// export function handleExternalActions();


//= ========== Linked Snippet Upgrade
export function buildLocalToRemoteActionPublicIdMap(args: {
  actions: Record<PublicId, Action>;
  snippetParentActionPublicId: PublicId;
}): Record<PublicId, PublicId> {
  const { actions, snippetParentActionPublicId } = args;

  // id map of all local child action ids to their remote action ids for the given snippet parent
  const localToRemoteActionPublicIdMap = Object.fromEntries(
    Object.values(actions).flatMap(action => {
      const localActionPublicId = action.publicId;

      if (!isChildOfSnippetAction({ action, snippetParentActionPublicId })) {
        return [];
      }

      const remoteActionPublicId = getRemoteActionPublicId({
        action,
        snippetParentActionPublicId,
      });

      return [[localActionPublicId, remoteActionPublicId]];
    }),
  );

  return localToRemoteActionPublicIdMap;
}


export function buildLocalToRemoteThresholdPublicIdMap(args: {
  localToRemoteActionPublicIdMap: Record<PublicId, PublicId>;
  localActions: Record<PublicId, Action>;
  localThresholds: Record<PublicId, Threshold>;
  remoteActions: Record<PublicId, Action>;
  parentSnippetActionPublicId: PublicId;
}): Record<PublicId, PublicId> {
  const {
    localToRemoteActionPublicIdMap, localActions, localThresholds,
    remoteActions, parentSnippetActionPublicId,
  } = args;


  const localToRemoteEntries = Object.entries(localToRemoteActionPublicIdMap)
    .flatMap(([localActionId, remoteActionId]) => {
      const localAction = localActions[localActionId];
      const remoteAction = remoteActions[remoteActionId];

      const localThresholdIds = extractThresholdIds(localAction.rules)
        .filter(id => {
          const threshold = localThresholds[id];
          return !threshold || !isSnippetGateThreshold({
            threshold,
            parentSnippetActionPublicId,
          });
        });
      const remoteThresholdIds = extractThresholdIds(remoteAction.rules);

      if (localThresholdIds.length !== remoteThresholdIds.length) {
        throw new Error('threshold count must be equal across local and remote rules');
      }

      return Array.from({ length: localThresholdIds.length }, (_, idx) => {
        const localThresholdId = localThresholdIds[idx];
        const remoteThresholdId = remoteThresholdIds[idx];

        return [localThresholdId, remoteThresholdId];
      });
    });

  return Object.fromEntries(localToRemoteEntries);
}

export function buildLocalToRemoteDataRoutePublicIdMap(args: {
  localActions: Record<PublicId, Action>;
  localDataRoutes: Record<PublicId, DataRoute>;
  localToRemoteActionPublicIdMap: Record<PublicId, PublicId>;
  remoteActions: Record<PublicId, Action>;
  remoteDataRoutes: Record<PublicId, DataRoute>;
}): Record<PublicId, PublicId> {
  const {
    localActions,
    localDataRoutes,
    localToRemoteActionPublicIdMap,
    remoteActions,
    remoteDataRoutes,
  } = args;

  const localToRemoteEntries = Object.entries(localToRemoteActionPublicIdMap)
    .flatMap(([localActionId, remoteActionId]) => {
      const localAction = localActions[localActionId];
      const remoteAction = remoteActions[remoteActionId];

      if (localAction.type !== remoteAction.type) {
        throw new Error('local and remote action types must match');
      }

      switch (localAction.type) {
        case ActionType.MATRIX_MODEL:
          return getLocalToRemoteDataRoutePublicIdForMatrixAction({
            localAction,
            remoteAction,
          });

        case ActionType.OUTPUT:
          return getLocalToRemoteDataRoutePublicIdForOutputAction({
            localAction,
            remoteAction,
          });

        case ActionType.SERVICE:
          return getLocalToRemoteDataRoutePublicIdsForServiceAction({
            localAction,
            localDataRoutes,
            localToRemoteActionPublicIdMap,
            remoteAction,
            remoteDataRoutes,
          });

        default:
          return [];
      }
    });

  return Object.fromEntries(localToRemoteEntries);
}


function getLocalToRemoteDataRoutePublicIdsForServiceAction(args: {
  localDataRoutes: Record<PublicId, DataRoute>;
  remoteDataRoutes: Record<PublicId, DataRoute>;
  localAction: Action;
  remoteAction: Action;
  localToRemoteActionPublicIdMap: Record<PublicId, PublicId>;
}): Array<[PublicId, PublicId]> {
  const {
    localAction,
    localDataRoutes,
    localToRemoteActionPublicIdMap,
    remoteAction,
    remoteDataRoutes,
  } = args;


  const localDataRoutesPointingToLocalAction = getDataRoutesForActions({
    action: localAction,
    dataRoutes: localDataRoutes,
  });


  const remoteDataRoutesPointingToRemoteAction = getDataRoutesForActions({
    action: remoteAction,
    dataRoutes: remoteDataRoutes,
  });


  if (localDataRoutesPointingToLocalAction.length
    !== remoteDataRoutesPointingToRemoteAction.length) {
    throw new Error('length of local and remote data routes id must be the same');
  }

  return Array.from({ length: localDataRoutesPointingToLocalAction.length }, (_, idx) => {
    const localDataRoute = localDataRoutesPointingToLocalAction[idx];

    const remoteSourceIdForLocal = localToRemoteActionPublicIdMap[localDataRoute.sourceId];
    const remoteTargetIdForLocal = localToRemoteActionPublicIdMap[localDataRoute.targetId];

    // not expected to change between local and remote, so direct access is enough
    const remoteCategoryNameForLocalCategoryName = localDataRoute.category.name;

    const remoteDataRoute = remoteDataRoutesPointingToRemoteAction.find(dataRoute => (
      dataRoute.sourceId === remoteSourceIdForLocal
      && dataRoute.targetId === remoteTargetIdForLocal
      && dataRoute.category.name === remoteCategoryNameForLocalCategoryName
    ));

    if (!remoteDataRoute) {
      throw new Error('could not find remote data route for local data route');
    }

    return [localDataRoute.publicId, remoteDataRoute.publicId];
  });
}


function getLocalToRemoteDataRoutePublicIdForOutputAction(args: {
  localAction: Action;
  remoteAction: Action;
}): Array<[PublicId, PublicId]> {
  const { localAction, remoteAction } = args;

  if (localAction.type !== remoteAction.type) {
    throw new Error('local and remote actions must have the same type');
  }

  const localExpression = localAction.output.expression;
  const remoteExpression = remoteAction.output.expression;

  if (localExpression.length !== remoteExpression.length) {
    throw new Error('local and remote expression lengths must be the same');
  }

  const localExpressionDataRouteIds = extractDataRouteIds(localExpression);
  const remoteExpressionDataRouteIds = extractDataRouteIds(remoteExpression);


  if (localExpressionDataRouteIds.length !== remoteExpressionDataRouteIds.length) {
    throw new Error('local and remote expression data route ids must be the same');
  }

  return Array.from({ length: localExpressionDataRouteIds.length }, (_, idx) => {
    const localDataRouteId = localExpressionDataRouteIds[idx];
    const remoteDataRouteId = remoteExpressionDataRouteIds[idx];

    return [localDataRouteId, remoteDataRouteId];
  });
}


function getLocalToRemoteDataRoutePublicIdForMatrixAction(args: {
  localAction: Action;
  remoteAction: Action;
}): Array<[PublicId, PublicId]> {
  const { localAction, remoteAction } = args;

  if (localAction.type !== remoteAction.type) {
    throw new Error('local and remote actions must have the same type');
  }

  const localDataRouteXPublicId = localAction.matrixModel.dataRouteX;
  const remoteDataRouteXPublicId = remoteAction.matrixModel.dataRouteX;

  const localDataRouteYPublicId = localAction.matrixModel.dataRouteY;
  const remoteDataRouteYPublicId = remoteAction.matrixModel.dataRouteY;

  const hasLocalDataRouteXPublicId = isPublicId(localDataRouteXPublicId);
  const hasRemoteDataRouteXPublicId = isPublicId(remoteDataRouteXPublicId);

  if (hasLocalDataRouteXPublicId !== hasRemoteDataRouteXPublicId) {
    throw new Error('local and remote data route X existence mismatch');
  }

  const hasLocalDataRouteYPublicId = isPublicId(localDataRouteYPublicId);
  const hasRemoteDataRouteYPublicId = isPublicId(remoteDataRouteYPublicId);

  if (hasLocalDataRouteYPublicId !== hasRemoteDataRouteYPublicId) {
    throw new Error('local and remote data route Y existence mismatch');
  }

  const hasDataRouteXPublicId = hasLocalDataRouteXPublicId && hasRemoteDataRouteXPublicId;
  const hasDataRouteYPublicId = hasLocalDataRouteYPublicId && hasRemoteDataRouteYPublicId;

  return [
    ...(hasDataRouteXPublicId ? [[localDataRouteXPublicId, remoteDataRouteXPublicId]] : []),
    ...(hasDataRouteYPublicId ? [[localDataRouteYPublicId, remoteDataRouteYPublicId]] : []),
  ] as Array<[PublicId, PublicId]>;
}


/**
 * Creates a "local next" snippet for a given current and next versions.
 * Terminology:
 * - remote current - the remote snippet version (in the library) that maps to an existing local version.
 * - local current - the local snippet version that maps to a remote snippet version from the library
 *    - local differs from remote in that all of its ids are different, and every child action has awareness of its snippet container
 *
 * - remote next - the next remote version (in the library) that the local current should be upgraded to.
 * - local next - the next safe version to use. the next local will have:
 *    - different ids from remote next
 *    - any element that exists in the local current will retain its id. this is crucial, as it lets us track
 *      operations (added/modified/removed/noop) on them across current and next local versions.
 */
export function buildLocalNextSnippet(args: {
  localCurrentSnippetData: SnippetVersion['data'];
  remoteCurrentSnippet: SnippetVersion;
  localSnippetActionPublicId: PublicId;
  remoteNextSnippet: SnippetVersion;
}): SnippetVersion {
  /**
   * we do not track remote threshold or data route ids, so this function infers them by mapping
   * ids across the local current and local remote versions:
   * think of current as v1 and next as v2 (we dont use those names as we could have any combination
   * of version numbers)
   *
   *  REMOTE                LOCAL
   *  (in library)          (in workflow)
   * ----------            ----------
   * | current |           | current |
   * ----------            ----------
   *     |                     |
   *     |                     |
   *     v                     v
   * ---------             ---------
   * | next  |             | next  |   <=== this is what we build in this function
   * ---------             ---------
   *
   *
   * here's how it's done:
   * - we build a map from local current to local remote. these 2 versions have the same
   *   structure (as we dont allow changes in local snippets without unlinking), making the
   *   mapping possible. **this is a crucial detail**.
   *    - actions, thresholds and dataroutes have their own logic for building this map
   * - we know that publicIds persist across versions. this means that ids in remote current
   *   and remote next are preserved, so any local current that maps to a remote current id
   *   also maps to an id in remote next, assuming the element was not removed in the remote
   *   next version.
   *    - if an id is missing in remote next, we can assume the element was deleted in remote next.
   *    - if an id is missing in local current, we can assume the element was added in remote next.
   *    - this function does not care about these operations. it just builds a snippet version.
   * - to compute the local next, we first need to prepare the remote next for insertion:
   *   - regenerate any ids for new elements, preserving any local ids from the current local version
   *   - run the snippet with regenerated ids through a preparation step to maintain & transfer, or
   *     create, any data that needs to be, such as container contexts, and gate rules.
   */

  const {
    localCurrentSnippetData,
    localSnippetActionPublicId,
    remoteCurrentSnippet,
    remoteNextSnippet,
  } = args;


  // id map mapping a remote id to its local id
  const currentRemoteToLocalIds = buildRemoteToLocalPublicIdMap({
    localSnippetData: localCurrentSnippetData,
    localSnippetActionPublicId,
    remoteSnippet: remoteCurrentSnippet,
  });

  // regenerate all ids in the incoming snippet but use local (resulting) ids for anything that
  // already exists in the current local version
  const {
    localToRemotePublicIds: nextDraftToRemoteIds,
    snippet: remappedRemoteNextSnippet,
  } = regenerateLinkedSnippetsPublicIds({
    snippet: remoteNextSnippet,
    remoteToLocalPublicIdOverrides: currentRemoteToLocalIds,
  });

  const localNextSnippet = prepareLinkedSnippetForUpgrade({
    snippet: remappedRemoteNextSnippet,
    localToRemotePublicIds: nextDraftToRemoteIds,
    parentSnippetActionPublicId: localSnippetActionPublicId,
    localCurrentSnippetData,
  });

  return localNextSnippet;
}


function buildRemoteToLocalPublicIdMap(args: {
  localSnippetData: SnippetVersion['data'];
  remoteSnippet: SnippetVersion;
  localSnippetActionPublicId: PublicId;
}) {
  const {
    localSnippetData,
    localSnippetActionPublicId,
    remoteSnippet,
  } = args;

  const currentLocalToRemoteActionIds = buildLocalToRemoteActionPublicIdMap({
    actions: localSnippetData.actions,
    snippetParentActionPublicId: localSnippetActionPublicId,
  });

  const currentLocalToRemoteThresholdIds = buildLocalToRemoteThresholdPublicIdMap({
    localActions: localSnippetData.actions,
    localThresholds: localSnippetData.thresholds,
    localToRemoteActionPublicIdMap: currentLocalToRemoteActionIds,
    remoteActions: remoteSnippet.data.actions,
    parentSnippetActionPublicId: localSnippetActionPublicId,
  });

  const currentLocalToRemoteDataRouteIds = buildLocalToRemoteDataRoutePublicIdMap({
    localActions: localSnippetData.actions,
    localDataRoutes: localSnippetData.dataRoutes,
    localToRemoteActionPublicIdMap: currentLocalToRemoteActionIds,
    remoteActions: remoteSnippet.data.actions,
    remoteDataRoutes: remoteSnippet.data.dataRoutes,
  });

  // create a map of the public ids between the current and local remote versions
  const currentLocalToRemoteIds = {
    ...currentLocalToRemoteActionIds,
    ...currentLocalToRemoteThresholdIds,
    ...currentLocalToRemoteDataRouteIds,
  };

  // invert the id - remote to local ids
  const currentRemoteToLocalIds = invertPublicIdMap(currentLocalToRemoteIds);

  return currentRemoteToLocalIds;
}


export function prepareLinkedSnippetForUpgrade(args: {
  snippet: SnippetVersion;
  localToRemotePublicIds: Record<PublicId, PublicId>;
  parentSnippetActionPublicId: PublicId;
  localCurrentSnippetData: SnippetVersion['data'];
}): SnippetVersion {
  const {
    snippet,
    localToRemotePublicIds,
    parentSnippetActionPublicId,
    localCurrentSnippetData,
  } = args;


  const snippetWithContainerContexts = transferOrAttachContainerContexts({
    snippet,
    parentSnippetActionPublicId,
    localCurrentSnippetData,
    localToRemotePublicIds,
  });

  const snippetWithUpgradedParentAction = upgradeParentSnippetAction({
    snippet: snippetWithContainerContexts,
    parentSnippetActionPublicId,
    localCurrentSnippetData,
  });


  const snippetWithGateRules = transferOrAttachGateRules({
    localCurrentSnippetData,
    parentSnippetActionPublicId,
    snippet: snippetWithUpgradedParentAction,
  });


  const sortedSnippet = sortSnippetInTopologicalOrder({
    snippet: snippetWithGateRules,
  });


  return sortedSnippet;
}


function upgradeParentSnippetAction(args: {
  snippet: SnippetVersion;
  parentSnippetActionPublicId: PublicId;
  localCurrentSnippetData: SnippetVersion['data'];
}): SnippetVersion {
  const { snippet, parentSnippetActionPublicId, localCurrentSnippetData } = args;

  if (snippet.metadata === null) {
    throw new Error('Snippet must contain metadata');
  }

  const currentSnippetAction = localCurrentSnippetData.actions[parentSnippetActionPublicId];
  if (!currentSnippetAction) {
    throw new Error('Parent snippet action not found');
  }

  const nextLocalSnippetAction: Action = {
    ...currentSnippetAction,
    snippet: {
      name: currentSnippetAction.snippet.name,
      actionOptionPublicId: currentSnippetAction.snippet.actionOptionPublicId,
      token: snippet.metadata.token,
      // upgrade the public version to the new version
      publicVersion: snippet.metadata.version,
    },
  };

  const updatedActions = [...Object.values(snippet.data.actions), nextLocalSnippetAction];

  return createSnippetVersion({
    actions: indexActionsByPublicId(updatedActions),
    thresholds: snippet.data.thresholds,
    dataRoutes: snippet.data.dataRoutes,
    tags: snippet.props.tags,
    outcomes: snippet.props.outcomes,
    services: snippet.props.services,
    publishedAttributes: snippet.props.publishedAttributes,
    reasonCodeGroups: snippet.props.reasonCodeGroups,
    lists: snippet.props.lists,
    metadata: snippet.metadata,
    snippets: snippet.props.snippets,
  });
}


function transferOrAttachContainerContexts(args: {
  snippet: SnippetVersion;
  parentSnippetActionPublicId: PublicId;
  localCurrentSnippetData: SnippetVersion['data'];
  localToRemotePublicIds: Record<PublicId, PublicId>;
}): SnippetVersion {
  const {
    snippet,
    parentSnippetActionPublicId,
    localCurrentSnippetData,
    localToRemotePublicIds,
  } = args;

  const childActionsWithContainerContext = Object.values(snippet.data.actions)
    .map(snippetAction => {
      const localCurrentAction = localCurrentSnippetData.actions[snippetAction.publicId];

      if (localCurrentAction) {
        // if there's a local action, use its container context
        return {
          ...snippetAction,
          containerContext: {
            ...localCurrentAction.containerContext,
          },
        };
      }

      const remoteActionPublicId = localToRemotePublicIds[snippetAction.publicId];
      if (!remoteActionPublicId) {
        throw new Error(`could not resolve remote action public id: ${snippetAction.publicId}`);
      }
      return {
        ...snippetAction,
        containerContext: createSnippetContainerContext({
          parentSnippetActionPublicId,
          remoteActionPublicId,
        }),
      };
    });

  return createSnippetVersion({
    actions: indexActionsByPublicId(childActionsWithContainerContext),
    thresholds: snippet.data.thresholds,
    dataRoutes: snippet.data.dataRoutes,
    tags: snippet.props.tags,
    outcomes: snippet.props.outcomes,
    services: snippet.props.services,
    publishedAttributes: snippet.props.publishedAttributes,
    reasonCodeGroups: snippet.props.reasonCodeGroups,
    lists: snippet.props.lists,
    metadata: snippet.metadata,
    snippets: snippet.props.snippets,
  });
}


function transferOrAttachGateRules(args: {
  snippet: SnippetVersion;
  parentSnippetActionPublicId: PublicId;
  localCurrentSnippetData: SnippetVersion['data'];
}): SnippetVersion {
  const { snippet, parentSnippetActionPublicId, localCurrentSnippetData } = args;


  // array of entries with actions that had new rules and their gate threshold
  const actionGateRuleEntries: Array<[Action, Threshold]> = Object.values(snippet.data.actions)
    .flatMap(remoteAction => {
      if (!actionRequiresGateRule(remoteAction)) {
        return [];
      }

      const localAction = localCurrentSnippetData.actions[remoteAction.publicId];

      if (localAction) {
        const currentGateThresholdPublicId = getGateRuleThresholdId({
          snippetChildActionId: localAction.publicId,
          snippetParentActionId: parentSnippetActionPublicId,
          thresholds: localCurrentSnippetData.thresholds,
        });

        if (!currentGateThresholdPublicId) {
          throw new Error('local current action must already have a gate rule');
        }

        const updatedRules = addThresholdToBooleanRule({
          thresholdId: currentGateThresholdPublicId,
          pathToPerformActionAt: booleanRuleStrings.AND_OPERATOR_STRING,
          originalRule: remoteAction.rules ?? {},
        });

        if (!updatedRules) {
          throw new Error('Unable to add gate rule to action');
        }

        const actionWithTransferedGateRule = {
          ...remoteAction,
          rules: updatedRules,
        };

        const currentGateThreshold = (
          localCurrentSnippetData.thresholds[currentGateThresholdPublicId]);
        return [[actionWithTransferedGateRule, currentGateThreshold]];
      }


      const gateThreshold = createSnippetGateThreshold({
        ownerActionId: remoteAction.publicId,
        parentActionId: parentSnippetActionPublicId,
      });

      const updatedRules = addThresholdToBooleanRule({
        thresholdId: gateThreshold.publicId,
        pathToPerformActionAt: booleanRuleStrings.AND_OPERATOR_STRING,
        originalRule: remoteAction.rules ?? {},
      });

      if (!updatedRules) {
        throw new Error('Unable to add gate rule to action');
      }

      const actionWithNewGateRule = {
        ...remoteAction,
        rules: updatedRules,
      };


      return [[actionWithNewGateRule, gateThreshold]];
    });


  const updatedActions = {
    ...snippet.data.actions,
    ...indexActionsByPublicId(
      actionGateRuleEntries.map(([action]) => action),
    ),
  };

  const updatedThresholds = {
    ...snippet.data.thresholds,
    ...indexThresholdsByPublicId(
      actionGateRuleEntries.map(([, threshold]) => threshold),
    ),
  };

  return createSnippetVersion({
    actions: updatedActions,
    thresholds: updatedThresholds,
    dataRoutes: snippet.data.dataRoutes,
    tags: snippet.props.tags,
    outcomes: snippet.props.outcomes,
    services: snippet.props.services,
    publishedAttributes: snippet.props.publishedAttributes,
    reasonCodeGroups: snippet.props.reasonCodeGroups,
    lists: snippet.props.lists,
    metadata: snippet.metadata,
    snippets: snippet.props.snippets,
  });
}


/**
 * Maps incoming snippet external actions to their equivalent workflow actions.
 * Returns a map of snippetActionPublicId → workflowActionPublicId for each collision.
 */
function computeExternalActionMergeMap(args: {
  workflow: {
    actions: Record<PublicId, Action>;
    thresholds: Record<PublicId, Threshold>;
    dataRoutes: Record<PublicId, DataRoute>;
  };
  snippet: SnippetVersion;
  snippetActionPublicId: PublicId;
}): Map<PublicId, PublicId> {
  const { workflow, snippet, snippetActionPublicId } = args;

  const actionMergeMap = new Map<PublicId, PublicId>();
  const externalWorkflowActions = Object
    .values(workflow.actions)
    .filter(action => isExternalActionType(action.type))
    // exclude external actions that belong to the snippet - otherwise a snippet
    // action would merge with itself
    .filter(action => !isChildOfSnippetAction({
      action,
      snippetParentActionPublicId: snippetActionPublicId,
    }));

  const workflowExternalActionsCollisionMap = getCollisionsWithinPrimitives({
    primitives: externalWorkflowActions,
  });

  for (const snippetAction of Object.values(snippet.data.actions)) {
    if (isExternalSnippetChildAction(snippetAction)) {
      const equivalencyKey = getEquivalencyKeyForAction({ action: snippetAction });
      const collidingActionPublicIds = workflowExternalActionsCollisionMap.get(equivalencyKey);

      if ((collidingActionPublicIds?.size ?? 0) > 0) {
        // there will be at most 1 collision on external actions, so this is safe
        const [equivalentWorkflowActionPublicId] = collidingActionPublicIds.values();

        // mark the action as merged
        actionMergeMap.set(snippetAction.publicId, equivalentWorkflowActionPublicId);
      }
    }
  }

  return actionMergeMap;
}


/**
 * Maps workflow actions that need to be unmerged from a snippet.
 * workflowActionId → snippetActionId (or undefined if the snippet action was removed).
 *
 * A WF action needs unmerging when:
 * - modify: the snippet action it was merged with is no longer equivalent
 * - remove: the snippet action it was merged with no longer exists in the new version
 */
function computeExternalActionUnmergeMap(args: {
  workflow: {
    actions: Record<PublicId, Action>;
    thresholds: Record<PublicId, Threshold>;
    dataRoutes: Record<PublicId, DataRoute>;
  };
  snippet: SnippetVersion;
  parentSnippetActionPublicId: PublicId;
}): Map<PublicId, PublicId | undefined> {
  const { workflow, snippet, parentSnippetActionPublicId } = args;

  // WF actions that could be unmerge targets: external type, child of this snippet
  const externalWorkflowActions = Object
    .values(workflow.actions)
    .filter(supportsActionMerging)
    // only consider snippet child actions present in the workflow as they are the only
    // ones that an action can unmerge from
    .filter(action => isChildOfSnippetAction({
      action,
      snippetParentActionPublicId: parentSnippetActionPublicId,
    }));

  const snippetExternalActions = Object
    .values(snippet.data.actions)
    .filter(isExternalSnippetChildAction);

  const actionUnmergeMap = new Map<PublicId, PublicId | undefined>();

  for (const wfAction of externalWorkflowActions) {
    const mergedSnippetAction = snippetExternalActions.find(snippetAction => (
      isSourceActionMergedIntoTargetAction({
        snippetParentActionPublicId: parentSnippetActionPublicId,
        source: snippetAction,
        target: wfAction,
      })
    ));

    if (!mergedSnippetAction) {
      // remove: snippet action no longer exists in v2
      actionUnmergeMap.set(wfAction.publicId, undefined);
      continue;
    }

    const wfActionEqKey = getEquivalencyKeyForAction({ action: wfAction });
    const snippetActionEqKey = getEquivalencyKeyForAction({ action: mergedSnippetAction });

    // modify: snippet action still exists but is no longer equivalent
    if (wfActionEqKey !== snippetActionEqKey) {
      actionUnmergeMap.set(wfAction.publicId, mergedSnippetAction.publicId);
    }
  }

  return actionUnmergeMap;
}


function getSnippetChildActions(args: {
  actions: Record<PublicId, Action>;
  snippetParentActionPublicId: PublicId;
}): Record<PublicId, Action> {
  const { actions, snippetParentActionPublicId } = args;

  return indexActionsByPublicId(Object.values(actions)
    .filter(action => (
      isChildOfSnippetAction({ action, snippetParentActionPublicId })
    )));
}

/**
 * Applies a snippet upgrade onto a workflow data graph and returns the result
 */
export function applySnippetVersionUpgrade(args: {
  workflow: {
    actions: Record<PublicId, Action>;
    thresholds: Record<PublicId, Threshold>;
    dataRoutes: Record<PublicId, DataRoute>;
  };
  snippet: SnippetVersion;
  localSnippetActionPublicId: PublicId;
}): {
    actions: Record<PublicId, Action>;
    thresholds: Record<PublicId, Threshold>;
    dataRoutes: Record<PublicId, DataRoute>;
  } {
  /**
     * runs through all of the changes between the incoming snippet and the snippet actions already
     * present in the workflow to determine what changed and how.
     *
     * a snippet action can be merged with an equivalent workflow action during an insertion (the
     * added action is equivalent) or a modification (the existing action has been modified to now be
     * equivalent). when that happens, we favor the the wf action and do not insert (or remove) the
     * snippet action from the result, and we update the wf action to contain the container context
     * entries for the incoming snippet action.
     *
     * additionally, any threshold or dataRoute that pointed to it must be resolved to
     * replace that pointer with the wf action that it was merged into - these could be owned by a
     * snippet or workflow action.
     *
     * thresholds and dataRoutes are *not* mergeable.
     *
     * an action can also be *un*merged if it was previously merged with a workflow action but the next
     * version of the action is no longer equivalent. workflow actions should also be unmerged if the
     * next version of the snippet no longer contains the action that it was once merged with.
     *
     * this function runs 3 main steps:
     * 1. compute the decisions - what to merge/unmerge, and what changed & how across versions.
     * 2. unmerge - edit the once-merged actions and split them into 2 when applicable
     * 3. apply delta map - add, update, or remove actions based on the incoming snippet
     */


  const {
    localSnippetActionPublicId,
    snippet,
    workflow,
  } = args;


  // clone the workflow data to mutate. this is our result
  const nextWorkflowGraphData = structuredClone({
    actions: workflow.actions,
    thresholds: workflow.thresholds,
    dataRoutes: workflow.dataRoutes,
  });


  // ==== Step 1: compute decisions ====
  const wfSnippetChildActions = getSnippetChildActions({
    actions: workflow.actions,
    snippetParentActionPublicId: localSnippetActionPublicId,
  });

  const actionDeltaMap = buildWFElementOperationMap({
    snippetElements: snippet.data.actions,
    workflowElements: wfSnippetChildActions,
  });

  // map of actions (ids) to merge
  const externalActionMergeMap = computeExternalActionMergeMap({
    workflow,
    snippet,
    snippetActionPublicId: localSnippetActionPublicId,
  });

  // map of action (ids) to unmerge
  const externalActionUnmergeMap = computeExternalActionUnmergeMap({
    parentSnippetActionPublicId: localSnippetActionPublicId,
    snippet,
    workflow,
  });


  // ==== Step 2: unmerge ====
  /**
   * for every snippet action to unmerge out, we need to:
   * 1. update the workflow action and remove the parent snippet entry from it
   * 2. add the action as a standalone action into the results IF the new action does not
   * happen to have an equivalency with another workflow action, and if the action actually
   * exists in the new version (it was not removed)
   */
  for (const [workflowActionId, snippetActionId] of externalActionUnmergeMap) {
    // split wf action
    const wfAction = nextWorkflowGraphData.actions[workflowActionId];
    nextWorkflowGraphData.actions[workflowActionId] = removeSnippetParentFromChildContainer(
      wfAction,
      localSnippetActionPublicId,
    );

    /**
     * snippetActionId is undefined when the snippet action was removed in the next version
     * so only add it as a standalone action if actually exists, and if if it won't be merged
     * into another WF action.
     */
    if (snippetActionId && !externalActionMergeMap.has(snippetActionId)) {
      nextWorkflowGraphData.actions[snippetActionId] = snippet.data.actions[snippetActionId];
    }
  }


  // ==== Step 3: apply delta map ====

  for (const addedOrModifiedActionId of [...actionDeltaMap.added, ...actionDeltaMap.modified]) {
    const snippetAction = snippet.data.actions[addedOrModifiedActionId];
    // an action will only be either added or modified
    const isModified = actionDeltaMap.modified.has(addedOrModifiedActionId);
    const toMerge = externalActionMergeMap.has(snippetAction.publicId);

    if (!toMerge) {
      // simply add it if no merging needed
      nextWorkflowGraphData.actions[snippetAction.publicId] = snippetAction;
    } else {
      const equivalentWorkflowActionPublicId = externalActionMergeMap.get(snippetAction.publicId);
      const equivalentWorkflowAction = workflow.actions[equivalentWorkflowActionPublicId];

      const snippetActionRemoteId = getRemoteActionPublicId({
        action: snippetAction,
        snippetParentActionPublicId: localSnippetActionPublicId,
      });

      const updatedAction = appendSnippetParent({
        action: equivalentWorkflowAction,
        parentSnippetActionPublicId: localSnippetActionPublicId,
        remoteActionPublicId: snippetActionRemoteId,
      });

      nextWorkflowGraphData.actions[equivalentWorkflowActionPublicId] = updatedAction;

      // if modified, we need to remove the original action as it's now merged
      if (isModified) {
        const {
          [addedOrModifiedActionId]: modifiedMergedActionToRemove,
          ...existingActions
        } = nextWorkflowGraphData.actions;
        nextWorkflowGraphData.actions = existingActions;
        /**
         * No need to clean up relevant data for action update:
         * - thresholds are cleaned up in the threshold delta map
         * - data routes are cleaned up in the data route delta map
         * If in the case, we need to clean up additional data outside of those two cases for action update,
         * we can use the modifyRelevantDataFromActionUpdate function with public id as path
         */
      }
    }
  }

  // manually remove all actions that are no longer present in the snippet
  for (const removedActionId of actionDeltaMap.removed) {
    // skip this WF action if it was unmerged above (kept in workflow, just unlinked from snippet)
    if (externalActionUnmergeMap.has(removedActionId)) continue;

    const {
      [removedActionId]: removedAction,
      ...existingActions
    } = nextWorkflowGraphData.actions;

    // note - this function runs the updates in-place
    modifyRelevantDataFromActionRemoval({
      originalTargetValue: removedAction,
      workflowData: nextWorkflowGraphData,
    });

    nextWorkflowGraphData.actions = existingActions;
  }


  // ==== Thresholds ====
  /**
   * resolve all snippet thresholds to update their references to point
   * to any merged action.
   * there's no need to resolve unmerged actions because the incoming snippet version
   * has thresholds that already point to that action
   */
  const resolvedSnippetThresholds = resolveThresholdParentReferences({
    actionIdMap: externalActionMergeMap,
    thresholds: Object.values(snippet.data.thresholds),
  });

  /**
   * a workflow threshold might point to a snippet action that gets merged during the update,
   * so those are resolved as well
   */
  const resolvedWorkflowThresholds = resolveThresholdParentReferences({
    actionIdMap: externalActionMergeMap,
    thresholds: Object.values(nextWorkflowGraphData.thresholds),
  });

  nextWorkflowGraphData.thresholds = {
    ...nextWorkflowGraphData.thresholds,
    ...indexThresholdsByPublicId(resolvedWorkflowThresholds),
    ...indexThresholdsByPublicId(resolvedSnippetThresholds),
  };

  const wfChildActionThresholds = indexThresholdsByPublicId(
    getThresholdsForActions({
      actions: Object.values(wfSnippetChildActions),
      thresholds: workflow.thresholds,
    }),
  );

  // compare workflow and snippet thresholds
  const thresholdDeltaMap = buildWFElementOperationMap({
    snippetElements: snippet.data.thresholds,
    workflowElements: wfChildActionThresholds,
  });

  // remove any thresholds that were removed in the incoming snippet version
  nextWorkflowGraphData.thresholds = indexThresholdsByPublicId(
    Object.values(nextWorkflowGraphData.thresholds)
      .filter(threshold => !thresholdDeltaMap.removed.has(threshold.publicId)),
  );

  // ==== DataRoutes =====
  /**
   * resolve all snippet dataRoutes to update their references to point
   * to any merged action.
   */
  const resolvedDataRoutes = resolveDataRouteSourceReferences({
    actionIdMap: externalActionMergeMap,
    dataRoutes: Object.values(snippet.data.dataRoutes),
  });


  /**
   * a workflow dataRoutes might point to a snippet action that gets merged during the update,
   * so those are resolved as well
   */
  const resolvedWorkflowDataRoutes = resolveDataRouteSourceReferences({
    actionIdMap: externalActionMergeMap,
    dataRoutes: Object.values(nextWorkflowGraphData.dataRoutes),
  });


  nextWorkflowGraphData.dataRoutes = {
    ...nextWorkflowGraphData.dataRoutes,
    ...indexDataRoutesByPublicId(resolvedWorkflowDataRoutes),
    ...indexDataRoutesByPublicId(resolvedDataRoutes),
  };

  const wfChildActionDataRoutes = indexDataRoutesByPublicId(
    getDataRoutesForActions({
      actions: Object.values(wfSnippetChildActions),
      dataRoutes: workflow.dataRoutes,
    }),
  );

  const dataRouteDeltaMap = buildWFElementOperationMap({
    snippetElements: snippet.data.dataRoutes,
    workflowElements: wfChildActionDataRoutes,
  });

  nextWorkflowGraphData.dataRoutes = indexDataRoutesByPublicId(
    Object.values(nextWorkflowGraphData.dataRoutes)
      .filter(dataRoute => !dataRouteDeltaMap.removed.has(dataRoute.publicId)),
  );

  return nextWorkflowGraphData;
}


export function buildWFElementOperationMap<T extends WFElement>(args: {
  workflowElements: Record<T['publicId'], T>;
  snippetElements: Record<T['publicId'], T>;
}): WFElementOperationMap {
  const { workflowElements, snippetElements } = args;

  const operationMap: WFElementOperationMap = {
    added: new Set(),
    modified: new Set(),
    removed: new Set(),
    noop: new Set(),
  };

  const allElements = [
    ...Object.values(snippetElements),
    ...Object.values(workflowElements),
  ] as T[];

  for (const element of allElements) {
    const { publicId: elementPublicId } = element;
    const wfElement = workflowElements[elementPublicId];
    const snElement = snippetElements[elementPublicId];

    // added
    if (!wfElement && snElement) {
      operationMap.added.add(elementPublicId);
    }

    // removed
    if (wfElement && !snElement) {
      operationMap.removed.add(elementPublicId);
    }

    // modified
    if (wfElement && snElement && areDifferent(wfElement, snElement)) {
      operationMap.modified.add(elementPublicId);
    }

    // noop
    if (wfElement && snElement && areEqual(wfElement, snElement)) {
      operationMap.noop.add(elementPublicId);
    }
  }

  return operationMap;
}
